/**
 * CicdSetup - admin page /cicd-setup
 *
 * Setup wizard for the GitHub Actions connection. The admin enters ONLY a
 * fine-grained GitHub PAT; the backend reads all SAP credentials (service key
 * and policy user) from the respective instance and pushes them to the
 * matching GitHub environment (DEV/QAS/PROD comes from the instance tags).
 *
 * Hard requirement: every repo involved MUST be private. If repoPrivate is
 * false, the setup button is locked and a clear warning is shown. The backend
 * also blocks with HTTP 409 before any credentials are transferred.
 *
 * Styling: Integration-in-One tokens, all cs-* classes scoped under .cs-root.
 */
import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import { useTranslation, Trans } from "react-i18next";
import { api, getToken } from "../utils/helpers";
import { useToast, ToastContainer } from "../components/ui";
import CicdImportInstanceForm from "../components/CicdImportInstanceForm";
import { STAGES, stageLabel } from "../utils/stages";

const CSS = `
/*
   CI/CD Setup (/cicd-setup)

   The page hangs under .iio; all --iio-* design tokens come from
   iio-tokens.css and are not redefined here.

   Structuring principle:
   - ONE surface level: the section (.cs-sect). Everything below it is
     structured with hairlines, spacing and typography, not more boxes.
   - ONE message pattern (.cs-msg) in four variants: info / ok / warn / crit.
     It replaces the earlier one-off styles.
   - Calm on load: no fly-in animations on page load. Motion only as the
     result of an action, plus exactly one designed completion moment (the
     checkmark draw, .cs-conv.is-anim).
   - Smallest font size 12px, status marks as a 2px bar plus a word.
*/

.cs-root{
  color:var(--iio-text-1);
  /* The signal color alone does not carry as text (4.3:1 contrast). Mixing it
     against the text color flips in the right direction with the theme: it
     gets lighter in dark mode, darker in light mode. Same pattern used
     elsewhere in the design system. */
  --cs-signal-text:color-mix(in srgb, var(--iio-signal) 72%, var(--iio-text-1));
}
.cs-ar{ color:var(--cs-signal-text); font-weight:700; }

/* --- Page header ---
   Compact: title and one sentence on the left, the privacy line on the
   right, a hairline below. No box, no restating the same point twice. */
.cs-ph{ margin-bottom:var(--iio-s-5); padding-bottom:var(--iio-s-4); border-bottom:1px solid var(--iio-border); }
.cs-ph .crumb{ font-size:12px; letter-spacing:.04em; color:var(--iio-text-3); margin-bottom:6px; }
.cs-ph .row{ display:flex; align-items:flex-start; justify-content:space-between; gap:var(--iio-s-8); flex-wrap:wrap; }
.cs-ph .lead-col{ min-width:0; flex:1 1 460px; }
.cs-ph h1{ margin:0; font-size:24px; font-weight:700; letter-spacing:-.02em; line-height:1.2; }
.cs-ph .sub{ margin-top:5px; font-size:13px; color:var(--iio-text-2); max-width:72ch; line-height:1.5; }
.cs-reassure{ display:flex; align-items:flex-start; gap:8px; flex:0 1 320px; margin-top:4px; font-size:12px; color:var(--iio-text-2); line-height:1.45; }
.cs-reassure b{ color:var(--iio-text-1); font-weight:600; }
.cs-reassure svg{ width:15px; height:15px; stroke:var(--iio-success); fill:none; stroke-width:1.9; stroke-linecap:round; stroke-linejoin:round; flex-shrink:0; margin-top:1px; }

.cs-grid{ display:flex; flex-direction:column; gap:var(--iio-s-4); }

/* --- Section and section header --- */
.cs-sect{ background:var(--iio-bg-2); border:1px solid var(--iio-border); border-radius:var(--iio-r-xl); padding:var(--iio-s-5) var(--iio-s-6); box-shadow:var(--iio-shadow-sm); }
.cs-sh{ display:flex; align-items:center; gap:11px; margin-bottom:var(--iio-s-5); }
.cs-no{ width:25px; height:25px; border-radius:50%; border:1.5px solid var(--iio-brand-ink); color:var(--iio-brand-ink); display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; flex-shrink:0; }
[data-theme="dark"] .cs-root .cs-no{ border-color:var(--iio-info); color:var(--iio-info); }
.cs-sh h2{ margin:0; font-size:15.5px; font-weight:700; }
.cs-sh .shsub{ font-size:12px; color:var(--iio-text-3); margin-top:1px; }

/* --- Form --- */
.cs-row2{ display:grid; grid-template-columns:1fr auto; gap:var(--iio-s-4); align-items:end; }
.cs-field{ display:flex; flex-direction:column; gap:6px; }
/* Field labels in sentence case: long all-caps lines like "Basis-Name für
   die Repositories" are hard to read and look like a generic template. */
.cs-field label{ font-size:12px; font-weight:600; letter-spacing:.02em; color:var(--iio-text-2); }
/* Consistent width for fields that stand alone. */
.cs-field.cs-w-feld{ max-width:440px; }

/* Checkbox theme-aware: brand-ink does not carry on dark surfaces (1.3:1). */
.cs-root input[type="checkbox"].cs-check{ accent-color:var(--iio-brand-ink); width:14px; height:14px; }
[data-theme="dark"] .cs-root input[type="checkbox"].cs-check{ accent-color:var(--iio-info); }
/* Initial color before the app script runs: the data-theme rules only apply
   once the attribute is set. For system dark mode the same values must apply
   even without the attribute, otherwise the first paint puts brand-ink on a
   dark surface. */
@media (prefers-color-scheme: dark){
  :root:not([data-theme="light"]) .cs-root .cs-no{ border-color:var(--iio-info); color:var(--iio-info); }
  :root:not([data-theme="light"]) .cs-root input[type="checkbox"].cs-check{ accent-color:var(--iio-info); }
}

/* Input field with all states: rest, hover, focus, disabled, invalid. */
.cs-input{ height:38px; padding:0 var(--iio-s-3); border:1px solid var(--iio-border); border-radius:var(--iio-r-sm); background:var(--iio-bg-1); font-size:14px; outline:none; transition:border-color var(--iio-tx-fast),box-shadow var(--iio-tx-fast),background var(--iio-tx-fast); width:100%; box-sizing:border-box; color:var(--iio-text-1); font-family:inherit; }
.cs-input::placeholder{ color:var(--iio-text-3); }
.cs-input:hover:not(:disabled):not(:focus){ border-color:var(--iio-text-4); }
.cs-input:focus{ border-color:var(--iio-brand-ink); box-shadow:0 0 0 3px color-mix(in srgb, var(--iio-brand-ink) 12%, transparent); background:var(--iio-bg-2); }
[data-theme="dark"] .cs-root .cs-input:focus{ border-color:var(--iio-info); box-shadow:0 0 0 3px color-mix(in srgb, var(--iio-info) 18%, transparent); }
.cs-input:disabled{ background:var(--iio-bg-3); color:var(--iio-text-3); cursor:not-allowed; }
.cs-input.is-invalid{ border-color:var(--iio-critical); }
.cs-input.is-invalid:focus{ border-color:var(--iio-critical); box-shadow:0 0 0 3px color-mix(in srgb, var(--iio-critical) 14%, transparent); }
.cs-hint{ font-size:12px; color:var(--iio-text-3); line-height:1.5; }
.cs-hint b{ color:var(--iio-text-2); font-weight:600; }
/* Success and error as a tint on the hint text. Kept because several places
   on the page color the hint instead of adding a box. */
.cs-hint.ok{ color:color-mix(in srgb, var(--iio-success) 70%, var(--iio-text-1)); }
.cs-hint.bad{ color:var(--iio-critical); }

/* Select field: the higher specificity overrides the global .iio select
   rule, so this field has the same geometry as .cs-input (38px, 14px, bg-1). */
.iio select.cs-sel{ height:38px; padding:0 32px 0 12px; border:1px solid var(--iio-border); border-radius:var(--iio-r-sm); background-color:var(--iio-bg-1); font-size:14px; font-weight:400; color:var(--iio-text-1); appearance:none; font-family:inherit; outline:none; width:100%; background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%237A8590' stroke-width='2' stroke-linecap='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E"); background-repeat:no-repeat; background-position:right 10px center; cursor:pointer; transition:border-color var(--iio-tx-fast),box-shadow var(--iio-tx-fast); }
.iio select.cs-sel:hover:not(:focus){ border-color:var(--iio-text-4); }
.iio select.cs-sel:focus{ border-color:var(--iio-brand-ink); box-shadow:0 0 0 3px color-mix(in srgb, var(--iio-brand-ink) 12%, transparent); }
[data-theme="dark"] .iio select.cs-sel:focus{ border-color:var(--iio-info); box-shadow:0 0 0 3px color-mix(in srgb, var(--iio-info) 18%, transparent); }
[data-theme="dark"] .cs-root .cs-sel{ background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236A7A82' stroke-width='2' stroke-linecap='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E"); }

/* --- Connected status (GitHub) ---
   Same visual language as the message pattern: tint, 1px border, a drawn
   icon. No more icon tile inside a box. */
.cs-conn{ display:flex; align-items:center; gap:12px; padding:12px 14px; border:1px solid var(--iio-border); border-radius:var(--iio-r-md); background:var(--iio-bg-1); }
.cs-conn.on{ border-color:color-mix(in srgb, var(--iio-success) 35%, var(--iio-border)); background:color-mix(in srgb, var(--iio-success) 5%, var(--iio-bg-1)); }
.cs-conn-ic{ display:flex; align-items:center; justify-content:center; color:var(--iio-text-2); flex-shrink:0; }
.cs-conn.on .cs-conn-ic{ color:var(--iio-success); }
.cs-conn-ic svg{ width:20px; height:20px; stroke:currentColor; fill:none; stroke-width:1.8; stroke-linecap:round; stroke-linejoin:round; }
.cs-conn-tx{ flex:1; min-width:0; }
.cs-conn-tx .t{ font-size:13.5px; font-weight:600; color:var(--iio-text-1); }
.cs-conn-tx .d{ font-size:12px; color:var(--iio-text-3); margin-top:2px; }
.cs-conn-tx .mono{ font-family:var(--iio-font-mono); font-size:12px; color:var(--iio-text-2); }

/* --- Buttons ---
   Grow from their padding, never a fixed width. Four roles (primary, dark,
   quiet, danger), every state fully defined. */
.cs-btn{ height:36px; padding:0 16px; border:1px solid transparent; border-radius:var(--iio-r-md); font-size:13px; font-weight:600; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; gap:8px; white-space:nowrap; flex-shrink:0; transition:background var(--iio-tx-fast),border-color var(--iio-tx-fast),color var(--iio-tx-fast); font-family:inherit; }
.cs-btn:focus-visible{ outline:2px solid var(--iio-brand-ink); outline-offset:2px; }
[data-theme="dark"] .cs-root .cs-btn:focus-visible{ outline-color:var(--iio-info); }
.cs-btn:active:not(:disabled){ transform:translateY(1px); }
.cs-btn:disabled{ cursor:not-allowed; }
/* Primary button: dark ink on the signal surface instead of white (5.4:1
   instead of 3.5:1). Hover brightens rather than darkens, so the label
   contrast increases. The two fixed color values here are intentional. */
.cs-btn.prim{ background:var(--iio-signal); color:#0A1418; }
.cs-btn.prim:hover:not(:disabled){ background:color-mix(in srgb, var(--iio-signal) 86%, #FFFFFF); }
.cs-btn.prim:disabled{ background:color-mix(in srgb, var(--iio-signal) 40%, var(--iio-bg-3)); color:rgba(255,255,255,.92); }
.cs-btn.dark{ background:var(--iio-brand-ink); color:#fff; }
.cs-btn.dark:hover:not(:disabled){ background:var(--iio-brand-ink-2); }
.cs-btn.dark:disabled{ background:color-mix(in srgb, var(--iio-brand-ink) 45%, var(--iio-bg-3)); color:rgba(255,255,255,.85); }
[data-theme="dark"] .cs-root .cs-btn.dark{ background:var(--iio-brand-ink-3); }
[data-theme="dark"] .cs-root .cs-btn.dark:hover:not(:disabled){ background:color-mix(in srgb, var(--iio-brand-ink-3) 80%, var(--iio-text-1)); }
.cs-btn.ghost{ background:transparent; color:var(--iio-text-2); border-color:var(--iio-border); }
.cs-btn.ghost:hover:not(:disabled){ background:var(--iio-bg-1); border-color:var(--iio-text-4); color:var(--iio-text-1); }
.cs-btn.ghost:disabled{ color:var(--iio-text-4); border-color:var(--iio-border-subtle); }
.cs-btn.danger{ background:transparent; color:var(--iio-critical); border-color:var(--iio-border); }
.cs-btn.danger:hover:not(:disabled){ background:color-mix(in srgb, var(--iio-critical) 8%, transparent); border-color:var(--iio-critical); }
.cs-btn.is-loading{ pointer-events:none; }
/* The spinner ring in a busy button follows the label color: the primary
   button uses dark ink, so a white ring would be the wrong tone there. */
.cs-btn.is-loading .cs-loading{ border-color:color-mix(in srgb, currentColor 35%, transparent); border-top-color:currentColor; }
.cs-btn svg{ width:16px; height:16px; stroke:currentColor; fill:none; stroke-width:2; stroke-linecap:round; stroke-linejoin:round; flex-shrink:0; }
.cs-linkbtn{ background:none; border:none; color:var(--iio-text-3); font-size:12px; cursor:pointer; text-decoration:underline; text-underline-offset:3px; padding:4px; font-family:inherit; }
.cs-linkbtn:hover{ color:var(--iio-text-1); }

/* --- Help box --- */

/* --- Import: search row and environment table ---
   The URL column gets the most weight; every truncated cell carries a title
   attribute. Below the minimum width, the table scrolls inside its OWN
   frame: nothing silently clips, and the page itself never scrolls
   sideways. */
.cs-imp-form{ display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr) auto; gap:var(--iio-s-4); align-items:end; }
.cs-imp-tbl{ margin-top:var(--iio-s-4); border:1px solid var(--iio-border); border-radius:var(--iio-r-lg); overflow-x:auto; }
.cs-imp-row{ display:grid; grid-template-columns:28px 92px 56px minmax(220px,1.6fr) minmax(150px,1fr) minmax(110px,.7fr); gap:10px; padding:10px 14px; border-top:1px solid var(--iio-border); align-items:center; font-size:12.5px; background:var(--iio-bg-1); min-width:720px; }
.cs-imp-row.head{ border-top:none; background:var(--iio-bg-2); font-size:12px; font-weight:600; letter-spacing:.08em; text-transform:uppercase; color:var(--iio-text-3); }
.cs-imp-row .mono{ font-family:var(--iio-font-mono); font-size:12px; color:var(--iio-text-2); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.cs-imp-row .stage{ font-weight:700; }
.cs-imp-row.dis{ opacity:.45; }

/* --- ONE message pattern ---
   Tinted surface, 1px border, a drawn icon, text. Four semantic variants.
   Used across the app for: the lock warning "Repository ist nicht privat"
   (crit), "Alle Repositories sind privat" (ok), aborting setup with "Es
   wurden keine Credentials übertragen" (crit), "Import fehlgeschlagen"
   (crit), preview warnings (warn, small), the reassurance in the PAT wizard
   (info), and the completion message. */
.cs-msg{ display:flex; align-items:flex-start; gap:10px; padding:11px 14px; border:1px solid; border-radius:var(--iio-r-md); font-size:12.5px; line-height:1.55; color:var(--iio-text-1); }
.cs-msg[hidden]{ display:none; } /* otherwise display:flex would override the hidden attribute */
.cs-msg > svg{ width:16px; height:16px; fill:none; stroke-width:2; stroke-linecap:round; stroke-linejoin:round; flex-shrink:0; margin-top:1px; }
.cs-msg .tx{ min-width:0; }
.cs-msg .tx b{ font-weight:600; color:var(--iio-text-1); }
.cs-msg ul{ margin:4px 0 0; padding-left:16px; }
.cs-msg.info{ border-color:color-mix(in srgb, var(--iio-info) 35%, var(--iio-border)); background:color-mix(in srgb, var(--iio-info) 5%, var(--iio-bg-1)); }
.cs-msg.info > svg{ stroke:var(--iio-info); }
.cs-msg.ok{ border-color:color-mix(in srgb, var(--iio-success) 35%, var(--iio-border)); background:color-mix(in srgb, var(--iio-success) 5%, var(--iio-bg-1)); }
.cs-msg.ok > svg{ stroke:var(--iio-success); }
.cs-msg.warn{ border-color:color-mix(in srgb, var(--iio-warning) 40%, var(--iio-border)); background:color-mix(in srgb, var(--iio-warning) 6%, var(--iio-bg-1)); }
.cs-msg.warn > svg{ stroke:var(--iio-warning); }
.cs-msg.crit{ border-color:color-mix(in srgb, var(--iio-critical) 40%, var(--iio-border)); background:color-mix(in srgb, var(--iio-critical) 5%, var(--iio-bg-1)); }
.cs-msg.crit > svg{ stroke:var(--iio-critical); }
/* Small variant for messages inside a stage or a list. */
.cs-msg.klein{ padding:8px 12px; font-size:12px; margin-top:10px; }

/* --- Loading state ---
   Spinning arc plus text. Used in the app for: "Lade Konfiguration ...",
   "Lade Vorschau ...", "Environments werden geprüft ...". */
.cs-load{ display:flex; align-items:center; gap:10px; padding:14px 2px; font-size:12.5px; color:var(--iio-text-3); }
.cs-load svg{ width:16px; height:16px; fill:none; stroke:var(--iio-text-3); stroke-width:2; stroke-linecap:round; animation:csSpin .8s linear infinite; }
/* Small ring inside a button and in rows that are busy during a run. */
.cs-loading{ display:inline-block; width:14px; height:14px; border:2px solid var(--iio-border); border-top-color:var(--cs-signal-text); border-radius:50%; animation:csSpin .8s linear infinite; }
@keyframes csSpin{ to{ transform:rotate(360deg); } }
@media (prefers-reduced-motion: reduce){ .cs-load svg, .cs-loading{ animation:none; } }

/* --- Project row ---
   No surface: a hairline row instead of yet another box. */
.cs-project{ display:flex; align-items:center; gap:12px; padding:10px 0; border-bottom:1px solid var(--iio-border-subtle); margin-bottom:var(--iio-s-4); }
.cs-project .lbl{ font-size:12px; letter-spacing:.1em; text-transform:uppercase; color:var(--iio-text-3); font-weight:600; }
.cs-project .val{ font-size:13px; color:var(--iio-text-1); font-weight:600; }
.cs-project .repo{ font-family:var(--iio-font-mono); font-size:12px; color:var(--iio-text-2); }

/* --- Stages (DEV / QAS / PRE-PROD / PROD) ---
   The proven principle stays: mark, name, status and sync in ONE header row,
   variables and secrets below it. But as a hairline list instead of a box
   inside a box. */
.cs-stage{ border-top:1px solid var(--iio-border-subtle); }
.cs-stage:last-child{ border-bottom:1px solid var(--iio-border-subtle); }
.cs-stage-head{ display:flex; align-items:center; gap:14px; padding:12px 0; }
/* Stage mark as a status sign per the design system: a 2px bar plus a word.
   The previous bordered box violated the no-pills rule. */
.cs-stage-badge{ display:inline-flex; align-items:center; gap:7px; font-size:12px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:var(--iio-text-2); }
.cs-stage-badge::before{ content:""; width:2px; height:12px; background:currentColor; }
.cs-stage-badge.dev{ color:var(--iio-info); }
.cs-stage-badge.qas{ color:var(--iio-text-2); }
.cs-stage-badge.prod{ color:var(--cs-signal-text); }
/* PRE-PROD sits between QAS and PROD: a warning tone, but not the production
   signal color. The tone is mixed against the text color because the
   warning token only reaches 3.4:1 as plain text. */
.cs-stage-badge.preprod{ color:color-mix(in srgb, var(--iio-warning) 65%, var(--iio-text-1)); }
.cs-stage-nm{ flex:1; min-width:0; }
.cs-stage-nm .n{ font-size:13.5px; font-weight:600; color:var(--iio-text-1); }
.cs-stage-nm .m{ font-size:12px; color:var(--iio-text-3); margin-top:1px; }
.cs-stage-status{ display:inline-flex; align-items:center; gap:7px; font-size:12px; font-weight:600; }
.cs-stage-status.ready{ color:color-mix(in srgb, var(--iio-success) 70%, var(--iio-text-1)); --cs-marke:var(--iio-success); }
.cs-stage-status.miss{ color:color-mix(in srgb, var(--iio-critical) 70%, var(--iio-text-1)); --cs-marke:var(--iio-critical); }
.cs-stage-status .dot{ width:2px; height:12px; border-radius:0; background:var(--cs-marke, currentColor); }
/* Sync mark as plain colored text instead of a bordered box. */
.cs-syncbadge{ margin-left:10px; font-size:12px; font-weight:700; white-space:nowrap; }
.cs-syncbadge.neu{ color:var(--iio-info); }
.cs-syncbadge.aktualisieren{ color:color-mix(in srgb, var(--iio-warning) 70%, var(--iio-text-1)); }
.cs-syncbadge.aktuell{ color:var(--iio-text-3); }
.cs-syncsummary{ display:flex; gap:16px; margin-bottom:12px; font-size:12.5px; font-weight:600; }
.cs-syncsummary .neu{ color:var(--iio-info); }
.cs-syncsummary .upd{ color:color-mix(in srgb, var(--iio-warning) 70%, var(--iio-text-1)); }
.cs-syncsummary .same{ color:var(--iio-text-3); }
.cs-stage-body{ padding:0 0 14px; }
/* Subheadings in sentence case: long all-caps lines are hard to read. */
.cs-stage-sub{ font-size:12px; font-weight:700; letter-spacing:.02em; color:var(--iio-text-3); margin:8px 0 6px; }
.cs-stage-sub:first-child{ margin-top:0; }
.cs-kv{ display:grid; grid-template-columns:190px 1fr; gap:4px 14px; font-size:12.5px; }
.cs-kv .k{ color:var(--iio-text-3); font-weight:600; }
.cs-kv .val{ color:var(--iio-text-1); word-break:break-all; }
.cs-kv .val.mono{ font-family:var(--iio-font-mono); font-size:12px; }
.cs-kv .val.mask{ font-family:var(--iio-font-mono); color:var(--iio-text-2); letter-spacing:.05em; }
.cs-kv .val.miss{ color:var(--iio-critical); font-style:italic; }
.cs-stage-meta{ display:flex; align-items:center; gap:16px; font-size:12px; color:var(--iio-text-3); margin-top:8px; }
.cs-stage-meta .policy{ display:inline-flex; align-items:center; gap:6px; }
.cs-stage-meta .policy .d{ width:2px; height:12px; border-radius:0; background:var(--iio-success); }
.cs-stage-meta .policy.off .d{ background:var(--iio-text-3); }

/* --- Extension announcement ---
   What the import writes into the customer's repository, announced BEFORE
   it happens. Large variant of the same message pattern: tinted surface,
   1px border, icon. Every state carries an icon AND a word; color alone
   does not carry the meaning. */
.cs-ext{ margin-top:var(--iio-s-4); border:1px solid color-mix(in srgb, var(--iio-info) 35%, var(--iio-border)); border-radius:var(--iio-r-md); background:color-mix(in srgb, var(--iio-info) 5%, var(--iio-bg-1)); padding:14px 16px; }
.cs-ext.is-aktuell{ border-color:color-mix(in srgb, var(--iio-success) 35%, var(--iio-border)); background:color-mix(in srgb, var(--iio-success) 5%, var(--iio-bg-1)); }
.cs-ext.is-unbekannt{ border-color:var(--iio-border); background:var(--iio-bg-1); }
.cs-ext .h{ display:flex; align-items:center; gap:8px; font-size:13px; font-weight:600; color:var(--iio-text-1); }
.cs-ext .h svg{ width:16px; height:16px; stroke:var(--iio-info); fill:none; stroke-width:2; stroke-linecap:round; stroke-linejoin:round; flex-shrink:0; }
.cs-ext.is-aktuell .h svg{ stroke:var(--iio-success); }
.cs-ext.is-unbekannt .h svg{ stroke:var(--iio-text-3); }
.cs-ext .lead{ margin-top:6px; font-size:12.5px; color:var(--iio-text-2); line-height:1.55; }
.cs-ext ul{ margin:10px 0 0; padding:0; list-style:none; display:grid; gap:6px; }
.cs-ext li{ display:grid; grid-template-columns:auto minmax(0,1fr); gap:8px; align-items:baseline; font-size:12px; color:var(--iio-text-2); }
.cs-ext li .pfad{ font-family:var(--iio-font-mono); font-size:12px; color:var(--iio-text-1); word-break:break-all; }
.cs-ext li .zw{ color:var(--iio-text-3); }
/* Mark for the action: new, update, unchanged. As a word, not a fill.
   65% mixed against the text color, not 75%: these marks sit on a tinted
   surface, where the 75% mix of the warning token drops to 4.35:1. At 65%
   it is 5.08:1. */
.cs-ext li .akt{ font-weight:600; font-size:12px; text-transform:uppercase; letter-spacing:.04em; white-space:nowrap; }
.cs-ext li .akt.neu{ color:color-mix(in srgb, var(--iio-info) 65%, var(--iio-text-1)); }
.cs-ext li .akt.aktualisieren{ color:color-mix(in srgb, var(--iio-warning) 65%, var(--iio-text-1)); }
.cs-ext li .akt.unveraendert{ color:var(--iio-text-3); }

/* --- Empty state ---
   "Keine getaggten Instanzen in diesem Space", "Keine Environments im
   Repository gefunden". */
.cs-empty{ text-align:center; padding:32px 16px; color:var(--iio-text-3); font-size:13px; border:1px dashed var(--iio-border); border-radius:var(--iio-r-lg); background:var(--iio-bg-1); }
.cs-empty b{ display:block; color:var(--iio-text-2); font-size:14px; font-weight:600; margin-bottom:4px; }

/* --- Action row at the bottom of a section --- */
.cs-actionrow{ display:flex; align-items:center; justify-content:space-between; gap:var(--iio-s-4); margin-top:var(--iio-s-4); padding-top:var(--iio-s-4); border-top:1px solid var(--iio-border-subtle); flex-wrap:wrap; }
.cs-actionrow .info{ font-size:12px; color:var(--iio-text-3); line-height:1.5; }
.cs-actionrow .info b{ color:var(--iio-text-2); }
.cs-actionrow .cs-field{ flex:1 1 260px; }
.cs-actionrow:has(.cs-field){ align-items:flex-end; }

/* --- Collapsible: result list and finished bootstrap ---
   A finished run collapses to a summary line (details), the list then runs
   at full length in the page flow. No second scroll area in the middle of
   the page. The row rules apply to both components, so they are written
   once. */
.cs-steps{ margin-top:var(--iio-s-4); border-top:1px solid var(--iio-border-subtle); padding-top:var(--iio-s-3); }
.cs-steps > summary,
.cs-fold > summary{ list-style:none; display:flex; align-items:center; gap:9px; cursor:pointer; font-size:13px; font-weight:600; color:var(--iio-text-1); padding:4px 0; }
.cs-steps > summary::-webkit-details-marker,
.cs-fold > summary::-webkit-details-marker{ display:none; }
.cs-steps > summary:focus-visible,
.cs-fold > summary:focus-visible{ outline:2px solid var(--iio-brand-ink); outline-offset:2px; border-radius:var(--iio-r-xs); }
[data-theme="dark"] .cs-root .cs-steps > summary:focus-visible,
[data-theme="dark"] .cs-root .cs-fold > summary:focus-visible{ outline-color:var(--iio-info); }
.cs-steps > summary .chev,
.cs-fold > summary .chev{ width:14px; height:14px; flex-shrink:0; color:var(--iio-text-3); transition:transform var(--iio-tx-med); }
.cs-steps[open] > summary .chev,
.cs-fold[open] > summary .chev{ transform:rotate(90deg); }
.cs-steps > summary .chev svg,
.cs-fold > summary .chev svg{ width:14px; height:14px; stroke:currentColor; fill:none; stroke-width:2; stroke-linecap:round; stroke-linejoin:round; display:block; }
.cs-steps > summary .zsm,
.cs-fold > summary .zsm{ font-weight:400; color:var(--iio-text-3); font-size:12px; }
.cs-steps > summary .okmark,
.cs-fold > summary .okmark{ width:16px; height:16px; flex-shrink:0; color:var(--iio-success); }
/* Counterpart for a failed run: "Einrichtung fehlgeschlagen", "Aufsetzen
   fehlgeschlagen". Without this mark, the summary line would only handle
   the success case. */
.cs-steps > summary .badmark,
.cs-fold > summary .badmark{ width:16px; height:16px; flex-shrink:0; color:var(--iio-critical); }
.cs-steps > summary .okmark svg,
.cs-fold > summary .okmark svg,
.cs-steps > summary .badmark svg,
.cs-fold > summary .badmark svg{ width:16px; height:16px; stroke:currentColor; fill:none; stroke-width:2.4; stroke-linecap:round; stroke-linejoin:round; display:block; }
.cs-steps .liste{ margin-top:6px; }
.cs-step{ display:flex; align-items:center; gap:10px; padding:6px 0; border-bottom:1px solid var(--iio-border-subtle); font-size:12.5px; }
.cs-step:last-child{ border-bottom:0; }
.cs-step .ic{ width:16px; height:16px; flex-shrink:0; display:flex; align-items:center; justify-content:center; }
.cs-step .ic svg{ width:14px; height:14px; stroke:currentColor; fill:none; stroke-width:2.4; stroke-linecap:round; stroke-linejoin:round; }
.cs-step.ok .ic{ color:var(--iio-success); }
.cs-step.bad .ic{ color:var(--iio-critical); }
.cs-step .nm{ flex:1; font-family:var(--iio-font-mono); font-size:12px; color:var(--iio-text-1); word-break:break-all; }
/* The HTTP column is named .http, not .status: the global .iio .status rule
   would otherwise prepend its own 2px vertical bar. */
.cs-step .http{ font-size:12px; color:var(--iio-text-3); font-family:var(--iio-font-mono); }
.cs-step .err{ font-size:12px; color:var(--iio-critical); margin-left:8px; }

/* --- Role lock (non-admin users) --- */
.cs-denied{ max-width:440px; margin:80px auto; text-align:center; color:var(--iio-text-2); }
.cs-denied h2{ font-size:20px; color:var(--iio-text-1); margin:0 0 8px; }

/* --- Guided creation (import wizard) ---
   Flattened: a section with a hairline on top instead of a box inside a
   box. */
.cs-wiz{ margin-top:var(--iio-s-5); border-top:1px solid var(--iio-border-subtle); padding-top:var(--iio-s-4); }
.cs-wiz-head .sub{ font-size:12px; color:var(--iio-text-3); margin-top:3px; line-height:1.5; }
.cs-wiz-rail{ display:flex; align-items:center; gap:10px; margin:16px 0 20px; }
.cs-wiz-node{ display:flex; align-items:center; gap:8px; }
.cs-wiz-dot{ width:26px; height:26px; border-radius:50%; border:2px solid var(--iio-border); display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; color:var(--iio-text-3); background:var(--iio-bg-0); transition:border-color .3s, color .3s; }
.cs-wiz-node.act .cs-wiz-dot{ border-color:var(--cs-signal-text); color:var(--cs-signal-text); }
.cs-wiz-node.done .cs-wiz-dot{ border-color:var(--iio-success); color:var(--iio-success); }
.cs-wiz-node .lbl{ font-size:12px; font-weight:700; letter-spacing:.06em; color:var(--iio-text-3); }
.cs-wiz-node.act .lbl{ color:var(--iio-text-1); }
.cs-wiz-conn{ flex:1; height:2px; background:var(--iio-border-subtle); position:relative; overflow:hidden; min-width:26px; border-radius:1px; }
.cs-wiz-conn i{ position:absolute; inset:0; background:var(--iio-success); transform:scaleX(0); transform-origin:left; transition:transform .45s cubic-bezier(.22,.61,.36,1); }
.cs-wiz-conn.fill i{ transform:scaleX(1); }
/* No styling of its own anymore (the step change no longer animates by
   itself); the class stays as a hook in the markup. */
.cs-wiz-step{ }
.cs-wiz-foot{ display:flex; align-items:center; justify-content:space-between; gap:12px; margin-top:16px; padding-top:14px; border-top:1px solid var(--iio-border-subtle); }
@keyframes csStepIn{ from{ opacity:0; transform:translateX(12px); } to{ opacity:1; transform:none; } }

/* Instance form inside the wizard. */
.cs-instform{ display:flex; flex-direction:column; gap:14px; }
.cs-instform .cs-paste{ min-height:64px; font-size:12px; line-height:1.5; }
/* Pre-filled fields: a calm, persistent marker instead of a color flash.
   The tint and the hint below it explain each other. */
.cs-root .cs-prefill{ background:color-mix(in srgb, var(--iio-info) 5%, var(--iio-bg-1)); }
.cs-prefill-hint{ font-size:12px; color:color-mix(in srgb, var(--iio-info) 55%, var(--iio-text-2)); }
.cs-req{ font-size:12px; font-weight:700; letter-spacing:.05em; text-transform:uppercase; color:var(--cs-signal-text); margin-left:6px; }
/* Field pairs in the wizard: the two-column rule this page was missing
   (.iio-fields-2 only exists under .iio-modal in iio-components.css). */
.cs-root .iio-fields-2{ display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:14px; }
@media(max-width:680px){ .cs-root .iio-fields-2{ grid-template-columns:1fr; } }
/* The global field label, also in sentence case. */
.iio .cs-root .iio-field-label{ text-transform:none; letter-spacing:.02em; }

/* --- Convergence check ---
   Flattened like the bootstrap section. The page loads quietly, the final
   state is just there. The checkmark draw is THE one designed completion
   moment, and only plays when .is-anim is set, i.e. when the result
   arrives. */
.cs-conv{ margin-top:var(--iio-s-5); border-top:1px solid var(--iio-border-subtle); padding-top:var(--iio-s-4); }
.cs-conv-rail{ display:flex; align-items:center; gap:10px; margin:14px 0; max-width:520px; }
.cs-conv-node{ display:flex; flex-direction:column; align-items:center; gap:6px; min-width:56px; }
.cs-conv-dot{ width:34px; height:34px; border-radius:50%; border:2px solid var(--iio-border); display:flex; align-items:center; justify-content:center; background:var(--iio-bg-0); color:var(--iio-text-4); }
.cs-conv-node.on .cs-conv-dot{ border-color:var(--iio-success); }
.cs-conv-dot svg{ width:16px; height:16px; stroke:var(--iio-success); fill:none; stroke-width:2.6; stroke-linecap:round; stroke-linejoin:round; }
.cs-conv.is-anim .cs-conv-node.on .cs-conv-dot svg{ stroke-dasharray:24; stroke-dashoffset:24; animation:csDraw .45s ease-out forwards; animation-delay:inherit; }
@keyframes csDraw{ to{ stroke-dashoffset:0; } }
.cs-conv-node .lbl{ font-size:12px; font-weight:700; letter-spacing:.06em; color:var(--iio-text-3); }
.cs-conv-node .st{ font-size:12px; color:color-mix(in srgb, var(--iio-warning) 65%, var(--iio-text-1)); }
.cs-conv-conn{ flex:1; height:2px; background:var(--iio-border-subtle); position:relative; overflow:hidden; border-radius:1px; }
.cs-conv-conn.on i{ position:absolute; inset:0; background:var(--iio-success); }
.cs-conv.is-anim .cs-conv-conn.on i{ transform:scaleX(0); transform-origin:left; animation:csFill .5s cubic-bezier(.22,.61,.36,1) forwards; animation-delay:inherit; }
@keyframes csFill{ to{ transform:scaleX(1); } }
/* Completion message: text and follow-up action stacked instead of on one
   line. A button glued to a long paragraph looks misaligned. */
.cs-conv-msg{ font-size:12.5px; color:var(--iio-text-2); line-height:1.6; display:flex; flex-direction:column; align-items:flex-start; gap:var(--iio-s-4); }
.cs-conv-msg b{ color:var(--iio-text-1); }
.cs-conv-msg.ok b{ color:var(--iio-success); }
.cs-conv + .cs-msg{ margin-top:var(--iio-s-4); }

/* --- Scenario choice and path views ---
   Two equal-weight choices with radio-button character. The mark is named
   .marke, not .tag: the global .iio .tag rule would otherwise turn it into
   a full-width bordered box. No hover jump. */
.cs-scn-row{ display:grid; grid-template-columns:1fr 1fr; gap:var(--iio-s-4); }
@media(max-width:860px){ .cs-scn-row{ grid-template-columns:1fr; } }
.cs-scn{ text-align:left; display:flex; flex-direction:column; gap:9px; padding:18px; background:var(--iio-bg-1); border:1px solid var(--iio-border); border-radius:var(--iio-r-lg); cursor:pointer; font-family:inherit; transition:border-color var(--iio-tx-fast), background var(--iio-tx-fast); }
.cs-scn:hover:not(:disabled):not(.is-active){ border-color:var(--iio-text-4); }
.cs-scn:focus-visible{ outline:2px solid var(--iio-brand-ink); outline-offset:2px; }
[data-theme="dark"] .cs-root .cs-scn:focus-visible{ outline-color:var(--iio-info); }
.cs-scn:disabled{ opacity:.55; cursor:not-allowed; }
.cs-scn.is-active{ border-color:var(--cs-signal-text); box-shadow:inset 0 0 0 1px var(--iio-signal); }
.cs-scn .ic{ width:34px; height:34px; border-radius:var(--iio-r-md); border:1px solid var(--iio-border); display:flex; align-items:center; justify-content:center; color:var(--iio-text-2); }
.cs-scn.is-active .ic{ color:var(--cs-signal-text); border-color:var(--cs-signal-text); }
.cs-scn .ic svg{ width:16px; height:16px; stroke:currentColor; fill:none; stroke-width:2; stroke-linecap:round; stroke-linejoin:round; }
.cs-scn .t{ font-size:14.5px; font-weight:700; color:var(--iio-text-1); }
.cs-scn .d{ font-size:12px; color:var(--iio-text-3); line-height:1.55; }
.cs-scn .marke{ font-size:12px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:var(--iio-text-4); }
.cs-scn.is-active .marke{ color:var(--cs-signal-text); }
/* Like .cs-wiz-step: a hook with no styling of its own, the path switch is
   silent. */
/* The container for the chosen path holds several sections. Without its own
   layout they would stack with no gap, while the sections above it carry the
   grid's spacing, so the page would end up with two different rhythms. Same
   value as .cs-grid, so there is exactly ONE spacing rule. */
.cs-view{ display:flex; flex-direction:column; gap:var(--iio-s-4); }

/* --- Mode selection (extended / SAP only) ---------------------------------
   Cards reuse the cs-scn pattern, the table is a plain hairline list. */
.cs-modus{ margin-top:var(--iio-s-5); border-top:1px solid var(--iio-border-subtle); padding-top:var(--iio-s-4); }
.cs-modus h3{ margin:0 0 4px; font-size:14px; font-weight:600; color:var(--iio-text-1); }
.cs-modus .sub{ font-size:12.5px; color:var(--iio-text-3); line-height:1.6; margin-bottom:14px; }
.cs-vgl{ margin-top:var(--iio-s-4); }
.cs-vgl-head{ font-size:12px; font-weight:700; letter-spacing:.02em; color:var(--iio-text-3); margin-bottom:6px; }
.cs-vgl-row{ display:grid; grid-template-columns:130px 1fr 1fr; gap:10px; padding:8px 0; border-top:1px solid var(--iio-border-subtle); font-size:12.5px; align-items:baseline; }
.cs-vgl-row .k{ font-weight:600; color:var(--iio-text-2); }
.cs-vgl-row .v{ color:var(--iio-text-1); }
.cs-vgl-row .v.dim{ color:var(--iio-text-3); }
@media(max-width:600px){ .cs-vgl-row{ grid-template-columns:1fr; } }

/* --- Bootstrap (scenario 1, starting from zero) ---
   Flattened: no box inside a box, a section with a hairline on top,
   structure through spacing and ONE heading level. */
.cs-boot{ margin-top:var(--iio-s-5); margin-bottom:var(--iio-s-4); border-top:1px solid var(--iio-border-subtle); padding-top:var(--iio-s-4); }
.cs-boot h3, .cs-wiz-head h3, .cs-conv h3{ margin:0 0 4px; font-size:14px; font-weight:600; color:var(--iio-text-1); }
.cs-boot .sub{ font-size:12.5px; color:var(--iio-text-3); line-height:1.6; margin-bottom:14px; }
.cs-boot .mono{ font-family:var(--iio-font-mono); font-size:12px; color:var(--iio-text-2); }
.cs-boot-run{ margin-top:4px; }
/* The rule sits BETWEEN phases, not under the last one. Hence on top instead
   of on the bottom, and switched off on the first child: :last-of-type would
   only match the last phase if no other div followed it, but the error box
   and the step list come after it. */
.cs-boot-ph{ display:flex; align-items:center; gap:10px; padding:9px 0; border-top:1px solid var(--iio-border-subtle); font-size:13px; color:var(--iio-text-4); }
.cs-boot-run > .cs-boot-ph:first-child{ border-top:0; }
.cs-boot-ph.run{ color:var(--iio-text-1); }
.cs-boot-ph.done{ color:var(--iio-text-2); }
/* Failed phase: a cross instead of a checkmark, so a partial success does
   not look like a full one. The color carries the icon, the word stays
   readable. */
.cs-boot-ph.bad{ color:var(--iio-text-1); }
.cs-boot-ph .pic{ width:18px; height:18px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.cs-boot-ph .pic svg{ width:15px; height:15px; stroke:var(--iio-success); fill:none; stroke-width:2.4; stroke-linecap:round; stroke-linejoin:round; }
.cs-boot-ph.bad .pic svg{ stroke:var(--iio-critical); }
.cs-boot-steps{ margin:6px 0 6px 28px; border-left:1px solid var(--iio-border-subtle); padding-left:14px; }
.cs-boot-steps .row{ display:flex; align-items:center; gap:10px; padding:5px 0; font-size:12.5px; }
.cs-boot-steps .row .pic{ width:15px; height:15px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.cs-boot-steps .row .pic svg{ width:13px; height:13px; stroke:var(--iio-success); fill:none; stroke-width:2.4; stroke-linecap:round; stroke-linejoin:round; }
.cs-boot-steps .row .nm{ color:var(--iio-text-2); }
.cs-boot-steps .row .dt{ font-family:var(--iio-font-mono); font-size:12px; color:var(--iio-text-3); margin-left:auto; text-align:right; }

/* --- PAT wizard (GitHub token, step by step) --- */
.cs-pw{ margin-top:var(--iio-s-3); }
.cs-pw-lead{ margin-bottom:var(--iio-s-5); }
.cs-pw-lead h3{ margin:0 0 4px; font-size:16px; font-weight:700; color:var(--iio-text-1); }
.cs-pw-lead p{ margin:0; font-size:12.5px; color:var(--iio-text-3); line-height:1.5; }

.cs-pw-rail{ display:flex; align-items:flex-start; gap:0; margin:0 0 var(--iio-s-5); }
@media(max-width:760px){ .cs-pw-node:not(.act) .lbl{ display:none; } .cs-pw-node{ min-width:0; } }
.cs-pw-node{ display:flex; flex-direction:column; align-items:center; gap:8px; flex:0 0 auto; min-width:84px; background:none; border:none; cursor:pointer; font-family:inherit; padding:0; }
.cs-pw-dot{ width:34px; height:34px; border-radius:50%; border:2px solid var(--iio-border); background:var(--iio-bg-1); color:var(--iio-text-3); display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:700; transition:border-color .3s, color .3s, background .3s; flex-shrink:0; }
.cs-pw-node.act .cs-pw-dot{ border-color:var(--cs-signal-text); color:var(--cs-signal-text); background:color-mix(in srgb, var(--iio-signal) 10%, var(--iio-bg-1)); }
.cs-pw-node.done .cs-pw-dot{ border-color:var(--iio-success); color:var(--iio-success); }
.cs-pw-node.done .cs-pw-dot svg{ width:15px; height:15px; stroke:currentColor; fill:none; stroke-width:2.6; stroke-linecap:round; stroke-linejoin:round; }
.cs-pw-node .lbl{ font-size:12px; font-weight:600; letter-spacing:.01em; color:var(--iio-text-3); text-align:center; line-height:1.3; max-width:96px; }
.cs-pw-node.act .lbl{ color:var(--iio-text-1); }
.cs-pw-node.done .lbl{ color:var(--iio-text-2); }
.cs-pw-seg{ flex:1 1 auto; height:2px; background:var(--iio-border-subtle); margin-top:17px; min-width:12px; border-radius:1px; position:relative; overflow:hidden; }
.cs-pw-seg i{ position:absolute; inset:0; background:var(--iio-success); transform:scaleX(0); transform-origin:left; transition:transform .4s cubic-bezier(.22,.61,.36,1); }
.cs-pw-seg.fill i{ transform:scaleX(1); }

/* ONE column geometry across the wizard: the right column is always 280px
   wide, so the vertical edge of the mockup and the tips column line up. The
   content panel no longer has its own border; a hairline separates the tips
   column. Only the script triggers the motion on a step change. */
.cs-pw-main{ display:grid; grid-template-columns:minmax(0,1fr) 280px; gap:var(--iio-s-6); align-items:start; }
@media(max-width:920px){ .cs-pw-main{ grid-template-columns:1fr; } }
.cs-pw-panel{ min-width:0; }
.cs-pw-content.is-in{ animation:csStepIn .18s ease-out both; }
.cs-pw-stepno{ font-size:12px; font-weight:700; letter-spacing:.12em; text-transform:uppercase; color:var(--cs-signal-text); }
.cs-pw-title{ margin:4px 0 2px; font-size:16px; font-weight:700; color:var(--iio-text-1); }
.cs-pw-sub{ font-size:12.5px; color:var(--iio-text-3); line-height:1.5; margin-bottom:16px; }
.cs-pw-split{ display:grid; grid-template-columns:minmax(0,1fr) minmax(0,280px); gap:22px; align-items:start; }
@media(max-width:680px){ .cs-pw-split{ grid-template-columns:1fr; } }

.cs-pw-steps{ list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:13px; }
.cs-pw-steps li{ display:flex; gap:11px; align-items:flex-start; font-size:13px; color:var(--iio-text-2); line-height:1.5; }
.cs-pw-steps li .n{ width:20px; height:20px; border-radius:50%; border:1px solid var(--iio-border); color:var(--iio-text-3); font-size:12px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; margin-top:1px; }
.cs-pw-steps li b{ color:var(--iio-text-1); font-weight:600; }
.cs-pw-steps li .mono{ font-family:var(--iio-font-mono); font-size:12px; background:var(--iio-bg-3); padding:1px 5px; border-radius:var(--iio-r-xs); color:var(--iio-text-1); }

.cs-pw-shot{ border:1px solid var(--iio-border); border-radius:var(--iio-r-md); background:var(--iio-bg-2); overflow:hidden; }
.cs-pw-chrome{ display:flex; align-items:center; gap:6px; padding:8px 10px; background:var(--iio-bg-3); border-bottom:1px solid var(--iio-border-subtle); }
.cs-pw-chrome .d{ width:8px; height:8px; border-radius:50%; background:var(--iio-text-4); opacity:.55; flex-shrink:0; }
.cs-pw-chrome .url{ margin-left:8px; flex:1; height:19px; border-radius:var(--iio-r-xs); background:var(--iio-bg-1); border:1px solid var(--iio-border-subtle); font-family:var(--iio-font-mono); font-size:12px; color:var(--iio-text-3); display:flex; align-items:center; padding:0 8px; overflow:hidden; white-space:nowrap; }
.cs-pw-scr{ padding:16px; display:flex; flex-direction:column; gap:9px; min-height:158px; }
.cs-pw-scr.mid{ align-items:center; justify-content:center; text-align:center; gap:12px; }
.cs-pw-scr .cap{ font-size:12px; font-weight:700; color:var(--iio-text-1); margin-bottom:2px; }
.cs-pw-mk{ width:30px; height:30px; color:var(--iio-text-2); }
.cs-pw-mk svg{ width:30px; height:30px; }
.cs-pw-lbl{ font-size:12px; font-weight:600; letter-spacing:.02em; color:var(--iio-text-2); }
.cs-pw-fld{ height:26px; border:1px solid var(--iio-border); border-radius:var(--iio-r-xs); background:var(--iio-bg-1); }
.cs-pw-fld.txt{ display:flex; align-items:center; justify-content:space-between; padding:0 10px; font-family:var(--iio-font-mono); font-size:12px; color:var(--iio-text-2); }
.cs-pw-fld.txt svg{ width:12px; height:12px; stroke:var(--iio-text-3); fill:none; stroke-width:2; stroke-linecap:round; stroke-linejoin:round; }
/* Recreation of the GitHub button: a darker green so the white label reaches
   5.3:1. The fixed color value is intentional here, since it quotes a
   third-party UI and is not a state of this page. */
.cs-pw-gbtn{ align-self:flex-start; height:27px; padding:0 13px; border-radius:var(--iio-r-xs); background:#1F7A47; color:#fff; font-size:12px; font-weight:700; display:inline-flex; align-items:center; justify-content:center; }
.cs-pw-perm{ display:flex; align-items:center; justify-content:space-between; gap:10px; padding:8px 0; border-top:1px solid var(--iio-border-subtle); font-size:12px; color:var(--iio-text-2); }
/* Same trap as with the tips: the first div is the caption .cap. */
.cs-pw-scr .cap + .cs-pw-perm{ border-top:none; }
.cs-pw-perm .rw{ font-size:12px; font-weight:700; color:var(--iio-success); }
.cs-pw-chk{ width:18px; height:18px; border-radius:var(--iio-r-xs); background:var(--iio-success); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.cs-pw-chk svg{ width:12px; height:12px; stroke:#fff; fill:none; stroke-width:3; stroke-linecap:round; stroke-linejoin:round; }
.cs-pw-radio{ display:flex; align-items:flex-start; gap:9px; padding:9px 10px; border:1px solid var(--iio-border); border-radius:var(--iio-r-sm); }
.cs-pw-radio.on{ border-color:var(--cs-signal-text); }
.cs-pw-radio .rc{ width:15px; height:15px; border-radius:50%; border:2px solid var(--iio-border); flex-shrink:0; margin-top:1px; position:relative; }
.cs-pw-radio.on .rc{ border-color:var(--cs-signal-text); }
.cs-pw-radio.on .rc::after{ content:''; position:absolute; inset:3px; border-radius:50%; background:var(--iio-signal); }
.cs-pw-radio .rt b{ font-size:12px; color:var(--iio-text-1); display:block; }
.cs-pw-radio .rt span{ font-size:12px; color:var(--iio-text-3); }
.cs-pw-note{ font-size:12px; color:var(--iio-text-3); margin-top:6px; line-height:1.5; }
.cs-pw-note b{ color:var(--iio-text-2); }

.cs-pw-connect{ display:flex; flex-direction:column; gap:10px; }
.cs-pw-connect .row{ display:flex; gap:10px; }
/* The connect button needs space above and below, otherwise it gets
   squeezed between the input field and the reassurance text. */
.cs-pw-connect > .cs-btn{ margin-top:6px; margin-bottom:6px; }

/* The tips live in their own card. The padding all around is the point:
   without it the text runs to the edge and looks clipped. */
.cs-pw-tips{ border:1px solid var(--iio-border); border-radius:var(--iio-r-lg); background:var(--iio-bg-2); padding:18px 20px; }
@media(max-width:920px){ .cs-pw-tips{ margin-top:var(--iio-s-4); } }
.cs-pw-tips .th{ display:flex; align-items:center; gap:8px; font-size:12.5px; font-weight:700; color:var(--iio-text-1); padding-bottom:12px; margin-bottom:2px; border-bottom:1px solid var(--iio-border-subtle); }
.cs-pw-tips .th svg{ width:16px; height:16px; stroke:var(--iio-signal); fill:none; stroke-width:1.9; stroke-linecap:round; stroke-linejoin:round; }
.cs-pw-tip{ display:flex; gap:11px; padding:10px 0; border-top:1px solid var(--iio-border-subtle); }
/* The rule separates tip from tip, not the heading from the first tip. Do
   NOT use :first-of-type: that matches the first div among the siblings,
   which is the heading .th, so the rule would hit nothing and the first tip
   would keep its border. */
.cs-pw-tips .th + .cs-pw-tip{ border-top:none; }
.cs-pw-tip .ti{ width:24px; height:24px; border-radius:var(--iio-r-sm); border:1px solid var(--iio-border); display:flex; align-items:center; justify-content:center; flex-shrink:0; color:var(--iio-text-3); }
.cs-pw-tip .ti svg{ width:13px; height:13px; stroke:currentColor; fill:none; stroke-width:1.9; stroke-linecap:round; stroke-linejoin:round; }
.cs-pw-tip .tt b{ font-size:12px; color:var(--iio-text-1); display:block; margin-bottom:2px; }
.cs-pw-tip .tt span{ font-size:12px; color:var(--iio-text-3); line-height:1.45; }

.cs-pw-nav{ display:flex; align-items:center; justify-content:space-between; gap:12px; margin-top:var(--iio-s-5); }
.cs-pw-dots{ display:flex; gap:7px; }
.cs-pw-dots i{ width:7px; height:7px; border-radius:50%; background:var(--iio-border); transition:background .25s, transform .25s; }
.cs-pw-dots i.on{ background:var(--iio-signal); transform:scale(1.3); }

/* --- Progressive disclosure ---
   The page grows with each action. Hidden sections exist in the markup and
   only appear after the triggering click. Arrival means a rise and fade-in
   with an arrival curve; leaving happens without delay: motion explains the
   state change, it does not decorate. */
.flow-hidden{ display:none; }
.flow-enter{ animation:csFlowIn .34s cubic-bezier(.16,1,.3,1) both; }
@keyframes csFlowIn{ from{ opacity:0; transform:translateY(14px); } to{ opacity:1; transform:none; } }

@media(prefers-reduced-motion:reduce){
  .cs-root *{ animation:none!important; transition:none!important; }
  .flow-enter{ animation:none; }
  .cs-conv.is-anim .cs-conv-node.on .cs-conv-dot svg{ stroke-dashoffset:0; }
  .cs-conv.is-anim .cs-conv-conn.on i{ transform:scaleX(1); }
}
`;

// Uses fetch() directly instead of the api() helper so a 409 response body
// (visibility, steps) can still be read. api() throws on non-2xx responses
// and the extra fields are lost with the exception.
async function runSetup(spaceId, opts) {
  const t = getToken();
  const payload = { spaceId };
  if (opts?.transportModus) payload.transportModus = opts.transportModus;
  const r = await fetch("/api/cicd/setup", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(t ? { Authorization: `Bearer ${t}` } : {}) },
    body: JSON.stringify(payload),
  });
  const body = await r.json().catch(() => ({}));
  return { status: r.status, body };
}

// --- PAT wizard: 7 steps to a fine-grained GitHub token ---
// Kept deliberately short (2-3 lines per step). An earlier version was one
// help block with six dense paragraphs that overwhelmed users; now each step
// walks through a mini guide plus a "screenshot" mockup.
//
// This only holds the scaffolding: step order and how many guide lines each
// one carries. The text itself lives in the catalog under pat.schritt.<key>.
// GitHub UI labels quoted there ("Generate new token", "Resource owner",
// "Read and write", ...) stay in English in both languages, otherwise users
// would not recognize them in the real GitHub UI.
const PAT_STEPS = [
  { key: "oeffnen", zeilen: 3 },
  { key: "benennen", zeilen: 3 },
  { key: "owner", zeilen: 2 },
  { key: "zugriff", zeilen: 2 },
  { key: "rechte", zeilen: 3 },
  { key: "secrets", zeilen: 2 },
  { key: "testen", zeilen: 3 },
];

const PAT_TIPS = [
  {
    key: "name",
    icon: <svg viewBox="0 0 24 24"><path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><path d="M7 7h.01"/></svg>,
  },
  {
    key: "ablauf",
    icon: <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>,
  },
  {
    key: "minimal",
    icon: <svg viewBox="0 0 24 24"><path d="M12 2 4 5v6c0 5 3.4 9.4 8 11 4.6-1.6 8-6 8-11V5l-8-3z"/><path d="m9 12 2 2 4-4"/></svg>,
  },
];

const chk = <span className="cs-pw-chk"><svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg></span>;

// "Screenshot" mockup per step (schematic, theme-aware, no bitmaps).
// Anything already in English here is a direct quote of the GitHub UI and
// stays literal in both languages. Only the explanatory prose runs through
// the catalog.
function patShot(i, t) {
  switch (i) {
    case 0:
      return (
        <div className="cs-pw-shot">
          <div className="cs-pw-chrome"><span className="d"/><span className="d"/><span className="d"/><span className="url">github.com/login</span></div>
          <div className="cs-pw-scr mid">
            <span className="cs-pw-mk"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 1.5A10.5 10.5 0 0 0 8.68 22c.53.1.72-.23.72-.5v-1.8c-2.93.64-3.55-1.25-3.55-1.25-.48-1.22-1.17-1.55-1.17-1.55-.96-.65.07-.64.07-.64 1.06.08 1.62 1.09 1.62 1.09.94 1.62 2.47 1.15 3.07.88.1-.68.37-1.15.67-1.42-2.34-.27-4.8-1.17-4.8-5.2 0-1.15.41-2.09 1.09-2.83-.11-.27-.47-1.34.1-2.8 0 0 .89-.28 2.9 1.08a10 10 0 0 1 5.28 0c2-1.36 2.89-1.08 2.89-1.08.58 1.46.22 2.53.11 2.8.68.74 1.09 1.68 1.09 2.83 0 4.04-2.47 4.93-4.82 5.19.38.33.72.98.72 1.98v2.93c0 .28.19.61.73.5A10.5 10.5 0 0 0 12 1.5z"/></svg></span>
            <div className="cs-pw-fld" style={{ width: "100%" }}/>
            <div className="cs-pw-fld" style={{ width: "100%" }}/>
            <div className="cs-pw-gbtn" style={{ alignSelf: "stretch" }}>Sign in</div>
          </div>
        </div>
      );
    case 1:
      return (
        <div className="cs-pw-shot">
          <div className="cs-pw-chrome"><span className="d"/><span className="d"/><span className="d"/><span className="url">github.com/settings/tokens</span></div>
          <div className="cs-pw-scr">
            <div className="cap">New fine-grained token</div>
            <div className="cs-pw-lbl">Token name</div>
            <div className="cs-pw-fld"/>
            <div className="cs-pw-lbl">{t("schaubild.beschreibungOptional")}</div>
            <div className="cs-pw-fld" style={{ height: 40 }}/>
            <div className="cs-pw-gbtn" style={{ marginTop: 4 }}>Generate token</div>
          </div>
        </div>
      );
    case 2:
      return (
        <div className="cs-pw-shot">
          <div className="cs-pw-chrome"><span className="d"/><span className="d"/><span className="d"/><span className="url">Resource owner</span></div>
          <div className="cs-pw-scr">
            <div className="cap">Resource owner</div>
            <div className="cs-pw-lbl">{t("schaubild.kontoOrganisation")}</div>
            <div className="cs-pw-fld txt">{t("schaubild.deinKonto")}<svg viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg></div>
            <div className="cs-pw-note">{t("schaubild.ownerHinweis")}</div>
          </div>
        </div>
      );
    case 3:
      return (
        <div className="cs-pw-shot">
          <div className="cs-pw-chrome"><span className="d"/><span className="d"/><span className="d"/><span className="url">Repository access</span></div>
          <div className="cs-pw-scr">
            <div className="cap">Repository access</div>
            <div className="cs-pw-radio"><span className="rc"/><span className="rt"><b>All repositories</b><span>{t("schaubild.alleRepos")}</span></span></div>
            <div className="cs-pw-radio on"><span className="rc"/><span className="rt"><b>Only select repositories</b><span>{t("schaubild.auswahlRepos")}</span></span></div>
          </div>
        </div>
      );
    case 4:
      return (
        <div className="cs-pw-shot">
          <div className="cs-pw-chrome"><span className="d"/><span className="d"/><span className="d"/><span className="url">Repository permissions</span></div>
          <div className="cs-pw-scr">
            <div className="cap">Repository permissions</div>
            {/* Keep this list in sync with pat.schritt.rechte - a token built from the
                picture alone must work. Workflows is required to commit workflow files
                ("Actions" alone does not cover that). */}
            {["Actions", "Administration", "Contents", "Environments", "Workflows"].map(p => (
              <div className="cs-pw-perm" key={p}><span>{p}</span><span style={{ display: "flex", alignItems: "center", gap: 8 }}><span className="rw">Read and write</span>{chk}</span></div>
            ))}
          </div>
        </div>
      );
    case 5:
      return (
        <div className="cs-pw-shot">
          <div className="cs-pw-chrome"><span className="d"/><span className="d"/><span className="d"/><span className="url">Secrets &amp; Variables</span></div>
          <div className="cs-pw-scr">
            <div className="cap">Secrets &amp; Variables</div>
            {["Secrets", "Variables"].map(p => (
              <div className="cs-pw-perm" key={p}><span>{p}</span><span style={{ display: "flex", alignItems: "center", gap: 8 }}><span className="rw">Read and write</span>{chk}</span></div>
            ))}
            <div className="cs-pw-note"><Trans t={t} i18nKey="schaubild.secretsHinweis" components={{ b: <b/> }}/></div>
          </div>
        </div>
      );
    default:
      return null;
  }
}

export default function CicdSetup({ user, spaces = [] }) {
  const { toasts, addToast } = useToast();
  const { t, i18n } = useTranslation(["cicdsetup", "gemeinsam"]);
  const gebiet = i18n.language === "en" ? "en-GB" : "de-DE";
  // t gets a new identity on every language switch. loadCfg and loadPreview
  // are wrapped in useCallback and called from effects; if t were in their
  // dependency array, switching language would reload config and preview and
  // wipe out the setup result along the way. The ref keeps the dependencies
  // stable while still resolving to the current language.
  const tRef = useRef(t);
  tRef.current = t;

  // GitHub connection
  const [cfg, setCfg] = useState(null); // { tokenSet, tokenMeta, template, projects }
  const [cfgLoading, setCfgLoading] = useState(true);
  const [pat, setPat] = useState("");
  // GitHub Enterprise Server: toggle and base URL belong to the token. Prefilled from
  // the config so switching the token keeps the stored server address.
  const [gheAktiv, setGheAktiv] = useState(false);
  const [gheUrl, setGheUrl] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [showPat, setShowPat] = useState(false);
  const [patStep, setPatStep] = useState(0); // PAT wizard step: 0..6

  // Space and preview
  const [spaceId, setSpaceId] = useState("");
  const [preview, setPreview] = useState(null); // { project, tokenSet, repoPrivate, stages }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewErr, setPreviewErr] = useState(null);

  // Setup result
  const [setupBusy, setSetupBusy] = useState(false);
  const [setupResult, setSetupResult] = useState(null); // { ok, project, steps, blocked?:bool, error? }

  // Import of an existing pipeline (GitHub -> webapp, the reverse of setup)
  const [impOwner, setImpOwner] = useState("");
  const [impRepo, setImpRepo] = useState("");
  const [impPreview, setImpPreview] = useState(null); // { repoPrivate, environments, workflow, suggestedSpaceName }
  const [impLoading, setImpLoading] = useState(false);
  const [impSel, setImpSel] = useState({}); // env name -> selected
  const [impSpaceName, setImpSpaceName] = useState("");
  const [impBusy, setImpBusy] = useState(false);
  const [impResult, setImpResult] = useState(null); // { ok, spaceName, created, hint } | { ok:false, error }
  const [impWiz, setImpWiz] = useState(null); // guided creation: { envs, idx, forms, deferred }
  const [impSync, setImpSync] = useState(null); // convergence check: { loading, preview, spaceId, healing, error, skipped }
  const impFinishRef = useRef(false); // synchronous double-click guard for the last wizard step (impBusy state only takes effect after the re-render)

  // Scenario choice: "new" = set up a pipeline from scratch (customer only
  // has the webapp), "import" = connect an existing pipeline (customer
  // already has git). Existing users (at least one bound project) land
  // automatically on the "new" path, since preview and sync live there and
  // the everyday workflow should not need an extra click.
  const [scenario, setScenario] = useState(null);
  const scenarioAutoRef = useRef(false);

  // Progressive disclosure: the page grows with each click. The path choice
  // only appears once the GitHub connection is set up, and the chosen path
  // only after that choice. So the newly revealed section is not missed below
  // the fold, the page scrolls to it smoothly, but only after a real click,
  // never on initial page load.
  const wegRef = useRef(null);
  const wegViewRef = useRef(null);
  // Only a real click triggers the fly-in animation for the path choice.
  // Anyone opening the page with an existing connection just sees it sitting
  // there; the page never animates itself into place on load.
  const wegAnimRef = useRef(false);
  const rolleZu = useCallback((ref) => {
    const ruhig = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let erledigt = false;
    const hin = () => {
      // Only counts as done once the section is actually mounted. Otherwise a
      // premature attempt burns the one shot, and the fallback timer below
      // then bails out without ever having scrolled.
      if (erledigt || !ref.current) return;
      erledigt = true;
      ref.current.scrollIntoView({ behavior: ruhig ? "auto" : "smooth", block: "start" });
    };
    // Wait two frames: React only mounts the new section on the next render
    // pass, so there is nothing to scroll to before that.
    requestAnimationFrame(() => requestAnimationFrame(hin));
    // If the tab is backgrounded, requestAnimationFrame never fires at all.
    // The fallback timer catches the scroll instead of letting it silently
    // fail.
    setTimeout(hin, 120);
  }, []);
  const waehleWeg = useCallback((weg) => {
    setScenario(weg);
    rolleZu(wegViewRef);
  }, [rolleZu]);
  useEffect(() => {
    if (scenarioAutoRef.current || !cfg) return;
    scenarioAutoRef.current = true;
    const anyBound = Object.values(cfg.projects || {}).some(p => Array.isArray(p?.spaceIds) && p.spaceIds.length > 0);
    if (anyBound) setScenario(s => s || "new");
  }, [cfg]);

  // Transport mode: "erweitert" (default) or "sap" - chosen before bootstrap/setup.
  const [transportModus, setTransportModus] = useState("erweitert");

  // Bootstrap (scenario 1, "starting from zero"): create repos from the SAP
  // base template plus the extension.
  const [bootName, setBootName] = useState("");
  const [bootBusy, setBootBusy] = useState(false);
  const [bootPhase, setBootPhase] = useState(null); // "repos" | "envs" | "check" | "done"
  const [bootResult, setBootResult] = useState(null); // { ok, owner, actionsRepo, contentRepo, upstreamSha, steps } | { ok:false, error, steps }
  // Expanded state of the run log. Open while the run is in progress, then
  // collapsed once. If the user collapses it manually it stays collapsed,
  // otherwise every render pass would force it open again.
  const [bootOffen, setBootOffen] = useState(true);
  // Reason the run got stuck after the repo phase. Without this, the summary
  // line reported green success even though the environments were never set
  // up, since the toast about it had long since faded.
  const [bootFehler, setBootFehler] = useState(null);
  const bootRef = useRef(false);

  const isAdmin = user && user.role === "admin";

  const loadCfg = useCallback(async () => {
    setCfgLoading(true);
    try {
      const r = await api("/api/cicd/config");
      setCfg(r);
    } catch (e) {
      addToast(e.message || tRef.current("meldung.configFehler"), "error");
    }
    setCfgLoading(false);
  }, [addToast]);

  const loadPreview = useCallback(async (sid) => {
    if (!sid) { setPreview(null); setPreviewErr(null); return; }
    setPreviewLoading(true);
    setPreviewErr(null);
    setSetupResult(null);
    try {
      const r = await api(`/api/cicd/setup-preview?spaceId=${encodeURIComponent(sid)}`);
      setPreview(r);
    } catch (e) {
      setPreviewErr(e.message || tRef.current("meldung.vorschauFehler"));
      setPreview(null);
    }
    setPreviewLoading(false);
  }, []);

  useEffect(() => { if (isAdmin) loadCfg(); }, [isAdmin, loadCfg]);
  useEffect(() => {
    if (cfg?.baseUrl) { setGheAktiv(true); setGheUrl(cfg.baseUrl); }
    else { setGheAktiv(false); setGheUrl(""); }
  }, [cfg?.baseUrl]);
  useEffect(() => { if (isAdmin && spaceId) loadPreview(spaceId); }, [isAdmin, spaceId, loadPreview]);

  const connectPat = async () => {
    const token = pat.trim();
    if (!token) { addToast(t("meldung.patFehlt"), "error"); return; }
    if (gheAktiv && !gheUrl.trim()) { addToast(t("ghe.urlFehlt"), "error"); return; }
    setConnecting(true);
    try {
      const r = await api("/api/cicd/github-token", { method: "POST", body: JSON.stringify({ token, baseUrl: gheAktiv ? gheUrl.trim() : "" }) });
      addToast(t("meldung.verbundenAls", { login: r.login }), "success");
      setPat("");
      setShowPat(false);
      wegAnimRef.current = true; // set before loadCfg so the section animates in
      await loadCfg();
      if (spaceId) await loadPreview(spaceId);
      rolleZu(wegRef); // scroll the newly unlocked path choice into view
    } catch (e) {
      addToast(e.message || t("meldung.tokenUngueltig"), "error");
    }
    setConnecting(false);
  };

  const disconnectPat = async () => {
    if (!window.confirm(t("verbindung.trennenFrage"))) return;
    setConnecting(true);
    try {
      await api("/api/cicd/github-token", { method: "DELETE" });
      addToast(t("meldung.getrennt"), "success");
      setPatStep(0); // wizard starts over on the next connect attempt
      setScenario(null); // everything downstream depends on the connection
      scenarioAutoRef.current = false; // let the auto-selection run again on reconnect
      await loadCfg();
      if (spaceId) await loadPreview(spaceId);
    } catch (e) {
      addToast(e.message || t("meldung.trennenFehler"), "error");
    }
    setConnecting(false);
  };

  const runSetupClick = async () => {
    if (!spaceId) return;
    setSetupBusy(true);
    setSetupResult(null);
    try {
      const { status, body } = await runSetup(spaceId);
      if (status === 200) {
        setSetupResult({ ok: !!body.ok, project: body.project, steps: body.steps || [] });
        if (body.ok) addToast(t("meldung.envsEingerichtet"), "success");
        else addToast(t("meldung.envsTeilweise"), "error");
      } else if (status === 409) {
        // 409 has two possible causes: the repo is not private, or the space
        // is not bound to a project. A fixed message always named the first
        // cause and stayed silent about the second.
        setSetupResult({ ok: false, blocked: true, error: body.error, visibility: body.visibility, steps: body.steps || [] });
        addToast(body.error || t("meldung.einrichtungAbgebrochen"), "error");
        await loadPreview(spaceId); // refresh preview so the warning becomes visible
      } else {
        setSetupResult({ ok: false, error: body.error || `HTTP ${status}`, steps: body.steps || [] });
        addToast(body.error || `HTTP ${status}`, "error");
      }
    } catch (e) {
      setSetupResult({ ok: false, error: e.message || t("meldung.unbekannterFehler") });
      addToast(e.message || t("meldung.unbekannterFehler"), "error");
    }
    setSetupBusy(false);
  };

  const loadImportPreview = async () => {
    if (!impOwner.trim() || !impRepo.trim()) { addToast(t("meldung.ownerRepoFehlt"), "error"); return; }
    setImpLoading(true); setImpPreview(null); setImpResult(null);
    try {
      const r = await api(`/api/cicd/import-preview?owner=${encodeURIComponent(impOwner.trim())}&repo=${encodeURIComponent(impRepo.trim())}`);
      setImpPreview(r);
      const sel = {};
      (r.environments || []).forEach(e => { if (e.importable) sel[e.env] = true; });
      setImpSel(sel);
      if (!impSpaceName.trim()) setImpSpaceName(r.suggestedSpaceName || impRepo.trim());
    } catch (e) {
      addToast(e.message || t("meldung.analyseFehler"), "error");
    }
    setImpLoading(false);
  };

  // --- Guided creation (import): the familiar instance form per environment,
  //     pre-filled from git; secrets are required (fallback: add them later).
  //     The convergence check (sync diff) runs automatically after creation. ---
  const STAGE_ORD = Object.fromEntries(STAGES.map(s => [s.key, s.order]));

  const startGuided = () => {
    const chosen = (impPreview?.environments || []).filter(e => impSel[e.env] && e.importable)
      .sort((a, b) => (STAGE_ORD[a.stage] ?? 9) - (STAGE_ORD[b.stage] ?? 9));
    if (chosen.length === 0) { addToast(t("meldung.envWaehlen"), "error"); return; }
    if (!impSpaceName.trim()) { addToast(t("meldung.spaceNameFehlt"), "error"); return; }
    const forms = {};
    chosen.forEach(e => {
      const oauth = e.authType !== "basic";
      forms[e.env] = {
        // The description is written into the instance record and stays in
        // German for that reason: otherwise its language would depend on who
        // ran the import, and the instance list would mix German and English
        // rows.
        name: e.env, description: `Importiert aus GitHub ${impOwner.trim()}/${impRepo.trim()} (Environment ${e.env})`,
        authType: oauth ? "oauth" : "basic",
        tenantUrl: e.tenantUrl || "", tokenUrl: e.tokenUrl || "",
        clientId: oauth ? (e.apiUser || "") : "", clientSecret: "",
        username: oauth ? "" : (e.apiUser || ""), password: "",
        policyUsername: e.policyUser || "", policyPassword: "",
        _prefilled: { name: true, authType: true, tenantUrl: !!e.tenantUrl, tokenUrl: !!e.tokenUrl, clientId: oauth && !!e.apiUser, username: !oauth && !!e.apiUser, policyUsername: !!e.policyUser },
      };
    });
    setImpResult(null); setImpSync(null);
    setImpWiz({ envs: chosen, idx: 0, forms, deferred: {} });
  };

  const wizStepValid = (w, env, requireSecret) => {
    const f = w.forms[env.env];
    if (!f.name.trim() || !f.tenantUrl.trim()) return t("meldung.nameUrlFehlt");
    if (f.authType === "oauth" && (!f.tokenUrl.trim() || !f.clientId.trim())) return t("meldung.tokenClientFehlt");
    if (requireSecret) {
      const sec = f.authType === "oauth" ? f.clientSecret : f.password;
      if (!sec) return t("meldung.secretFehlt");
    }
    return null;
  };

  const wizNext = (defer) => {
    const w = impWiz; if (!w || impBusy) return;
    const env = w.envs[w.idx];
    const err = wizStepValid(w, env, !defer);
    if (err) { addToast(err, "error"); return; }
    const nw = { ...w, deferred: { ...w.deferred, [env.env]: !!defer } };
    if (w.idx === w.envs.length - 1) {
      if (impFinishRef.current) return;
      impFinishRef.current = true;
      setImpWiz(nw); finishGuided(nw);
    } else setImpWiz({ ...nw, idx: w.idx + 1 });
  };

  const finishGuided = async (w) => {
    setImpBusy(true); setImpResult(null);
    try {
      const instances = w.envs.map(e => {
        const f = w.forms[e.env]; const deferred = w.deferred[e.env];
        return {
          env: e.env, stage: e.stage, name: f.name.trim(), description: f.description,
          authType: f.authType, tenantUrl: f.tenantUrl.trim(), tokenUrl: f.tokenUrl.trim(),
          clientId: f.clientId.trim(), username: f.username.trim(),
          apiUser: f.authType === "basic" ? f.username.trim() : f.clientId.trim(),
          policyUser: f.policyUsername.trim() || undefined,
          ...(deferred ? {} : {
            clientSecret: (f.authType === "oauth" && f.clientSecret) ? f.clientSecret : undefined,
            password: (f.authType === "basic" && f.password) ? f.password : undefined,
            policyPassword: f.policyPassword || undefined,
          }),
        };
      });
      const r = await api("/api/cicd/import", { method: "POST", body: JSON.stringify({
        owner: impOwner.trim(), repo: impRepo.trim(), spaceName: impSpaceName.trim(),
        actionsRef: impPreview?.workflow?.actionsRef || undefined, instances, transportModus,
      }) });
      setImpResult(r); setImpWiz(null);
      addToast(t("meldung.spaceAngelegt", { space: r.spaceName, count: (r.created || []).length }), "success");
      if ((r.created || []).length > 0 && (r.created || []).every(c => c.credsComplete)) runConvergence(r.spaceId);
      else setImpSync({ skipped: true });
    } catch (e) {
      setImpResult({ ok: false, error: e.message || t("meldung.importFehler") });
      addToast(e.message || t("meldung.importFehler"), "error");
    }
    setImpBusy(false);
    impFinishRef.current = false;
  };

  const runConvergence = async (sid) => {
    setImpSync({ loading: true, spaceId: sid });
    try {
      const p = await api(`/api/cicd/setup-preview?spaceId=${sid}`);
      setImpSync({ loading: false, preview: p, spaceId: sid });
    } catch (e) {
      setImpSync({ loading: false, error: e.message || t("meldung.pruefungFehler"), spaceId: sid });
    }
  };

  const healSync = async () => {
    const sid = impSync?.spaceId; if (!sid || impSync.healing) return;
    setImpSync(s => ({ ...s, healing: true }));
    try {
      const { status, body } = await runSetup(sid);
      if (status >= 200 && status < 300 && body.ok !== false) { addToast(t("meldung.synchronisiert"), "success"); await runConvergence(sid); }
      else { addToast(body.error || t("meldung.syncFehlerHttp", { status }), "error"); setImpSync(s => ({ ...s, healing: false })); }
    } catch (e) {
      addToast(e.message || t("meldung.syncFehler"), "error");
      setImpSync(s => ({ ...s, healing: false }));
    }
  };

  const slugify = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);

  // Bootstrap chain: create repos (backend, one call) -> fill in environments
  // (existing setup endpoint) -> convergence check (same as after import).
  const runBootstrap = async () => {
    if (!spaceId) { addToast(t("meldung.spaceZuerst"), "error"); return; }
    if (bootRef.current) return; bootRef.current = true;
    setBootBusy(true); setBootResult(null); setBootFehler(null); setImpSync(null); setBootPhase("repos"); setBootOffen(true);
    try {
      const spaceNm = spaces.find(s => s.id === spaceId)?.name || "";
      // Do NOT name this "t": it would shadow the translation function for
      // the rest of the try block, which the success path calls two lines
      // further down.
      const tok = getToken();
      const r = await fetch("/api/cicd/bootstrap", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(tok ? { Authorization: `Bearer ${tok}` } : {}) },
        body: JSON.stringify({ spaceId, baseName: slugify(bootName) || slugify(spaceNm), transportModus }),
      });
      const body = await r.json().catch(() => ({}));
      if (r.ok && body.ok) {
        setBootResult(body);
        addToast(t("meldung.reposAngelegt", { owner: body.owner, actions: body.actionsRepo, content: body.contentRepo }), "success");
        setBootPhase("envs");
        const su = await runSetup(spaceId, { transportModus });
        if (su.status >= 200 && su.status < 300 && su.body.ok !== false) {
          setBootPhase("check");
          await runConvergence(spaceId);
        } else {
          const grund = su.body.error || t("meldung.envSetupFehlerHttp", { status: su.status });
          setBootFehler(grund);
          addToast(grund, "error");
        }
        setBootPhase("done"); setBootOffen(false); // a finished run collapses to the summary line
        await loadPreview(spaceId);
        await loadCfg(); // reload the binding so the space just set up is no longer selectable
      } else {
        setBootResult({ ok: false, error: body.error || `HTTP ${r.status}`, steps: body.steps || [] });
        setBootPhase(null);
        addToast(body.error || t("meldung.bootFehlerHttp", { status: r.status }), "error");
      }
    } catch (e) {
      setBootResult({ ok: false, error: e.message || t("meldung.bootFehler"), steps: [] });
      setBootPhase(null);
      addToast(e.message || t("meldung.bootFehler"), "error");
    }
    setBootBusy(false); bootRef.current = false;
  };

  if (user && user.role !== "admin") {
    return (
      <div className="cs-root">
        <style>{CSS}</style>
        <div className="cs-denied">
          <h2>{t("zugriff.titel")}</h2>
          <p>{t("zugriff.text")}</p>
        </div>
        <ToastContainer toasts={toasts} />
      </div>
    );
  }

  const tokenSet = !!cfg?.tokenSet;
  const tokenMeta = cfg?.tokenMeta;

  // Convergence check: rendered identically in BOTH scenarios, after the
  // bootstrap (scenario 1) and after guided creation (scenario 2).
  const convergencePanel = impSync && !impSync.skipped && (
    <div className="cs-conv is-anim">
      <h3>{t("konvergenz.titel")}</h3>
      {impSync.loading && <div className="cs-load"><svg viewBox="0 0 24 24"><path d="M21 12a9 9 0 1 1-2.64-6.36"/></svg> {t("konvergenz.laedt")}</div>}
      {impSync.error && (
        <div className="cs-msg warn" style={{ marginTop: "var(--iio-s-3)" }}>
          <svg viewBox="0 0 24 24"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><path d="M12 9v4M12 17h.01"/></svg>
          <span className="tx">{t("konvergenz.fehlgeschlagen", { grund: impSync.error })}</span>
        </div>
      )}
      {!impSync.loading && impSync.preview && (() => {
        const cStages = (impSync.preview.stages || []).filter(s => s.stage);
        const okS = (s) => (s.sync?.status || "aktuell") === "aktuell";
        const allCurrent = cStages.length > 0 && cStages.every(okS);
        return (
          <>
            <div className="cs-conv-rail">
              {cStages.map((s, i) => (
                <Fragment key={s.stage}>
                  {i > 0 && <span className={"cs-conv-conn" + (okS(cStages[i - 1]) && okS(s) ? " on" : "")} style={{ animationDelay: `${0.2 + i * 0.35}s` }}><i style={{ animationDelay: `${0.2 + i * 0.35}s` }}/></span>}
                  <span className={"cs-conv-node" + (okS(s) ? " on" : "")} style={{ animationDelay: `${i * 0.35}s` }}>
                    <span className="cs-conv-dot" style={{ animationDelay: `${i * 0.35}s` }}>
                      {okS(s) ? <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg> : "!"}
                    </span>
                    <span className="lbl">{stageLabel(s.stage)}</span>
                    {!okS(s) && <span className="st">{s.sync?.status === "neu" ? t("konvergenz.wirdAngelegt") : t("konvergenz.abweichend")}</span>}
                  </span>
                </Fragment>
              ))}
            </div>
            {allCurrent ? (
              <div className="cs-conv-msg ok">
                <span><b>{t("konvergenz.bereitTitel")}</b> {t("konvergenz.bereitText")}</span>
                <a className="cs-btn dark" href="/cicd-pipeline" style={{ textDecoration: "none" }}>{t("konvergenz.monitorOeffnen")}</a>
              </div>
            ) : (
              <div className="cs-conv-msg">
                {/* The status from the response ("neu", "aktualisieren", ...) is a value.
                    Its label is displayed; if it is unknown, the raw value is shown instead. */}
                <span>{t("konvergenz.abweichungen", { liste: cStages.filter(s => !okS(s)).map(s => `${stageLabel(s.stage)} (${t(`konvergenz.status.${s.sync?.status || "unbekannt"}`, { defaultValue: s.sync?.status || "" })})`).join(" · ") })}</span>
                <button type="button" className={"cs-btn prim" + (impSync.healing ? " is-loading" : "")} onClick={healSync} disabled={impSync.healing}>
                  {impSync.healing ? <span className="cs-loading"/> : null} {t("konvergenz.synchronisieren")}
                </button>
              </div>
            )}
          </>
        );
      })()}
    </div>
  );
  const project = preview?.project || null; // NO default fallback: a foreign space must not show a repo
  const noProject = !!preview && !preview.project; // space has no usable CI/CD project
  // The space is not actually bound to any project (configured=false). The
  // preview can still show an unbound default as a setup candidate (the
  // forSetup fallback), but "set up new" only counts the real binding:
  // without it, the bootstrap needs to be offered, otherwise a customer would
  // end up binding the shared reference repo instead of getting their own
  // repos.
  const notBound = !!preview && preview.configured === false;
  const syncSummary = preview?.syncSummary || null;
  const repoPrivate = preview?.repoPrivate; // true | false | null
  const stages = preview?.stages || [];
  const anyStages = stages.length > 0;
  // notBound has to be part of the gate: otherwise the bootstrap box and an
  // active sync button would show at the same time, and the button would
  // write this space's credentials into the shared reference repo (the
  // server now rejects that with 409, but the button should not invite it in
  // the first place). Unbound spaces get either "set up new" or "connect".
  const canSetup = tokenSet && !!spaceId && anyStages && repoPrivate !== false && !!project && !notBound && !setupBusy;

  // A space may only have one pipeline (hence the copy-spaces for additional
  // projects). Once a space is already bound to a CI/CD project, it no longer
  // belongs in the setup selection, otherwise it would look like a second
  // pipeline could be set up (the server rejects that anyway). Bound spaces
  // are listed separately below and are not selectable.
  const gebundeneSpaceIds = new Set(
    Object.values(cfg?.projects || {}).flatMap(p => (Array.isArray(p?.spaceIds) ? p.spaceIds : []))
  );
  const freieSpaces = spaces.filter(sp => !gebundeneSpaceIds.has(sp.id));
  const belegteSpaces = spaces.filter(sp => gebundeneSpaceIds.has(sp.id));

  return (
    <div className="cs-root">
      <style>{CSS}</style>

      <div className="cs-ph">
        <div className="crumb">{t("kopf.crumbAdmin")} <span className="cs-ar">&gt;&gt;</span> {t("kopf.crumbCicd")} <span className="cs-ar">&gt;&gt;</span> {t("kopf.crumbAktuell")}</div>
        <div className="row">
          <div className="lead-col">
            <h1>{t("kopf.titel")}</h1>
            <div className="sub">{t("kopf.sub")}</div>
          </div>
          <div className="cs-reassure">
            <svg viewBox="0 0 24 24"><path d="M12 2 4 5v6c0 5 3.4 9.4 8 11 4.6-1.6 8-6 8-11V5l-8-3z"/><path d="m9 12 2 2 4-4"/></svg>
            <span><Trans t={t} i18nKey="kopf.zusicherung" components={{ b: <b/> }}/></span>
          </div>
        </div>
      </div>

      <div className="cs-grid">

        {/* 1 GitHub connection */}
        <section className="cs-sect">
          <div className="cs-sh">
            <div className="cs-no">1</div>
            <div>
              <h2>{t("verbindung.titel")}</h2>
              <div className="shsub">{t("verbindung.sub")}</div>
            </div>
          </div>

          {cfgLoading ? (
            <div className="cs-load"><svg viewBox="0 0 24 24"><path d="M21 12a9 9 0 1 1-2.64-6.36"/></svg> {t("verbindung.laedt")}</div>
          ) : tokenSet ? (
            <>
              <div className="cs-conn on">
                <div className="cs-conn-ic">
                  <svg viewBox="0 0 24 24"><path d="M9 19c-4 1.5-4-2.5-6-3m12 5v-3.5c0-1 .1-1.4-.5-2 2.8-.3 5.5-1.4 5.5-6a4.6 4.6 0 0 0-1.3-3.2 4.3 4.3 0 0 0-.1-3.2s-1-.3-3.4 1.3a11.7 11.7 0 0 0-6.2 0C6.6 2.8 5.6 3.1 5.6 3.1a4.3 4.3 0 0 0-.1 3.2 4.6 4.6 0 0 0-1.3 3.2c0 4.6 2.7 5.7 5.5 6-.6.6-.6 1.2-.5 2V21"/></svg>
                </div>
                <div className="cs-conn-tx">
                  <div className="t"><Trans t={t} i18nKey="verbindung.verbundenAls" values={{ login: tokenMeta?.login || "?" }} components={{ mono: <span className="mono"/> }}/></div>
                  <div className="d">
                    {tokenMeta?.setAt ? t("verbindung.hinterlegtAm", { zeit: new Date(tokenMeta.setAt).toLocaleString(gebiet) }) : t("verbindung.tokenAktiv")}
                    {tokenMeta?.scopes?.length ? t("verbindung.scopes", { liste: tokenMeta.scopes.join(", ") }) : ""}
                    {cfg?.baseUrl ? ` · ${t("ghe.verbunden", { host: cfg.baseUrl.replace(/^https:\/\//, "") })}` : ""}
                  </div>
                </div>
                <button type="button" className="cs-btn danger" onClick={disconnectPat} disabled={connecting}>
                  <svg viewBox="0 0 24 24"><path d="M18 6 6 18M6 6l12 12"/></svg>
                  {t("verbindung.trennen")}
                </button>
              </div>
            </>
          ) : (
            <div className="cs-pw">
              <div className="cs-pw-lead">
                <h3>{t("pat.leadTitel")}</h3>
                <p>{t("pat.leadText")}</p>
              </div>

              <div className="cs-pw-rail">
                {PAT_STEPS.map((s, i) => (
                  <Fragment key={i}>
                    {i > 0 && <span className={"cs-pw-seg" + (i <= patStep ? " fill" : "")}><i/></span>}
                    <button type="button" className={"cs-pw-node" + (i === patStep ? " act" : i < patStep ? " done" : "")} onClick={() => setPatStep(i)}>
                      <span className="cs-pw-dot">{i < patStep ? <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg> : i + 1}</span>
                      <span className="lbl">{t(`pat.schritt.${s.key}.label`)}</span>
                    </button>
                  </Fragment>
                ))}
              </div>

              <div className="cs-pw-main">
                <div className="cs-pw-panel">
                  <div className="cs-pw-content" key={patStep}>
                    <div className="cs-pw-stepno">{t("pat.schrittVon", { nr: patStep + 1, gesamt: PAT_STEPS.length })}</div>
                    <div className="cs-pw-title">{t(`pat.schritt.${PAT_STEPS[patStep].key}.titel`)}</div>
                    <div className="cs-pw-sub">{t(`pat.schritt.${PAT_STEPS[patStep].key}.sub`)}</div>
                    <div className="cs-pw-split">
                      <div>
                        <ol className="cs-pw-steps">
                          {Array.from({ length: PAT_STEPS[patStep].zeilen }, (_, i) => (
                            <li key={i}><span className="n">{i + 1}</span><span>
                              <Trans t={t} i18nKey={`pat.schritt.${PAT_STEPS[patStep].key}.zeile${i + 1}`} components={{ b: <b/>, mono: <span className="mono"/> }}/>
                            </span></li>
                          ))}
                        </ol>
                        {patStep === 0 && (
                          <a className="cs-btn dark" href={gheAktiv && gheUrl.trim() ? `${gheUrl.trim().replace(/\/+$/, "")}/settings/tokens` : "https://github.com/settings/personal-access-tokens/new"} target="_blank" rel="noopener noreferrer" style={{ marginTop: 16, textDecoration: "none" }}>
                            <svg viewBox="0 0 24 24"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><path d="M15 3h6v6"/><path d="M10 14 21 3"/></svg>
                            {t("pat.tokenSeite")}
                          </a>
                        )}
                      </div>
                      <div>
                        {patStep === PAT_STEPS.length - 1 ? (
                          <div className="cs-pw-connect">
                            <div className="cs-pw-lbl">{t("pat.feldLabel")}</div>
                            <div className="row">
                              <input
                                className="cs-input"
                                type={showPat ? "text" : "password"}
                                value={pat}
                                onChange={e => setPat(e.target.value)}
                                placeholder="github_pat_..."
                                autoComplete="off"
                                spellCheck={false}
                                onKeyDown={e => { if (e.key === "Enter") connectPat(); }}
                              />
                              <button type="button" className="cs-btn ghost" onClick={() => setShowPat(s => !s)} title={showPat ? t("pat.verbergen") : t("pat.anzeigen")}>
                                {showPat
                                  ? <svg viewBox="0 0 24 24"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a19.4 19.4 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a19.86 19.86 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22"/></svg>
                                  : <svg viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
                              </button>
                            </div>
                            {/* GHE as an opt-in below the token field: the github.com
                                default stays free of extra fields. */}
                            <label className="cs-pw-lbl" style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginTop: 10 }}>
                              <input type="checkbox" className="cs-check" checked={gheAktiv} onChange={e => setGheAktiv(e.target.checked)}/>
                              {t("ghe.nutzen")}
                            </label>
                            {gheAktiv && (
                              <>
                                <div className="cs-pw-lbl" style={{ marginTop: 8 }}>{t("ghe.url")}</div>
                                <input
                                  className="cs-input"
                                  type="url"
                                  value={gheUrl}
                                  onChange={e => setGheUrl(e.target.value)}
                                  placeholder="https://github.example.com"
                                  autoComplete="off"
                                  spellCheck={false}
                                />
                                <div className="cs-msg info" style={{ marginTop: 8 }}>
                                  <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
                                  <span className="tx">{t("ghe.hinweis")}</span>
                                </div>
                              </>
                            )}
                            <button type="button" className={"cs-btn prim" + (connecting ? " is-loading" : "")} onClick={connectPat} disabled={connecting || !pat.trim() || (gheAktiv && !gheUrl.trim())}>
                              {connecting ? <span className="cs-loading" /> : <svg viewBox="0 0 24 24"><path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/></svg>}
                              {t("pat.verbinden")}
                            </button>
                            <div className="cs-msg info">
                              <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
                              <span className="tx">{t("pat.zusicherung")}</span>
                            </div>
                          </div>
                        ) : patShot(patStep, t)}
                      </div>
                    </div>
                  </div>
                </div>

                <aside className="cs-pw-tips">
                  <div className="th">
                    <svg viewBox="0 0 24 24"><path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.1V17h6v-.2c0-.8.4-1.6 1-2.1A7 7 0 0 0 12 2z"/></svg>
                    {t("pat.tippsTitel")}
                  </div>
                  {PAT_TIPS.map((tipp) => (
                    <div className="cs-pw-tip" key={tipp.key}>
                      <span className="ti">{tipp.icon}</span>
                      <span className="tt"><b>{t(`pat.tipp.${tipp.key}.titel`)}</b><span>{t(`pat.tipp.${tipp.key}.text`)}</span></span>
                    </div>
                  ))}
                </aside>
              </div>

              <div className="cs-pw-nav">
                <button type="button" className="cs-btn ghost" onClick={() => setPatStep(s => Math.max(0, s - 1))} disabled={patStep === 0}>
                  <svg viewBox="0 0 24 24"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                  {t("gemeinsam:aktion.zurueck")}
                </button>
                <div className="cs-pw-dots">{PAT_STEPS.map((_, i) => <i key={i} className={i === patStep ? "on" : ""}/>)}</div>
                <button
                  type="button"
                  className="cs-btn dark"
                  onClick={() => setPatStep(s => Math.min(PAT_STEPS.length - 1, s + 1))}
                  style={patStep === PAT_STEPS.length - 1 ? { visibility: "hidden" } : undefined}
                >
                  {t("gemeinsam:aktion.weiter")}
                  <svg viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </button>
              </div>
            </div>
          )}
        </section>

        {/* 2 Path choice: both paths end in the same, indistinguishable state.
            Only appears once the GitHub connection is set up; without it neither
            path leads anywhere, and a dead choice is worse than no choice. */}
        {!cfgLoading && tokenSet && (
        <section className={"cs-sect" + (wegAnimRef.current ? " flow-enter" : "")} ref={wegRef}>
          <div className="cs-sh">
            <div className="cs-no">2</div>
            <div>
              <h2>{t("weg.titel")}</h2>
              <div className="shsub">{t("weg.sub")}</div>
            </div>
          </div>
          <div className="cs-scn-row">
            <button type="button" className={"cs-scn" + (scenario === "new" ? " is-active" : "")} onClick={() => waehleWeg("new")} disabled={bootBusy || impBusy}>
              <span className="ic"><svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg></span>
              <span className="t">{t("weg.neuTitel")}</span>
              <span className="d">{t("weg.neuText")}</span>
              <span className="marke">{t("weg.neuMarke")}</span>
            </button>
            <button type="button" className={"cs-scn" + (scenario === "import" ? " is-active" : "")} onClick={() => waehleWeg("import")} disabled={bootBusy || impBusy}>
              <span className="ic"><svg viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/></svg></span>
              <span className="t">{t("weg.importTitel")}</span>
              <span className="d">{t("weg.importText")}</span>
              <span className="marke">{t("weg.importMarke")}</span>
            </button>
          </div>
        </section>
        )}

        {tokenSet && scenario === "new" && (<div className="cs-view" key="scn-new" ref={wegViewRef}>

        {/* 3 Space and preview */}
        <section className="cs-sect">
          <div className="cs-sh">
            <div className="cs-no">3</div>
            <div>
              <h2>{t("vorschau.titel")}</h2>
              <div className="shsub">{t("vorschau.sub")}</div>
            </div>
          </div>

          <div className="cs-field cs-w-feld" style={{ marginBottom: "var(--iio-s-4)" }}>
            <label>{t("vorschau.spaceLabel")}</label>
            <div className="cs-row2">
              <select className="cs-sel" value={spaceId} onChange={e => setSpaceId(e.target.value)}>
                <option value="">{t("vorschau.spaceWaehlen")}</option>
                {freieSpaces.map(sp => <option key={sp.id} value={sp.id}>{sp.name}</option>)}
                {belegteSpaces.length > 0 && (
                  <optgroup label={t("vorschau.belegteGruppe")}>
                    {belegteSpaces.map(sp => <option key={sp.id} value={sp.id} disabled>{sp.name}</option>)}
                  </optgroup>
                )}
              </select>
              <button type="button" className="cs-btn ghost" onClick={() => loadPreview(spaceId)} disabled={!spaceId || previewLoading} title={t("vorschau.neuLadenTitel")}>
                <svg viewBox="0 0 24 24"><path d="M23 4v6h-6M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
                {t("gemeinsam:aktion.aktualisieren")}
              </button>
            </div>
            {belegteSpaces.length > 0 && (
              <div className="cs-hint" style={{ marginTop: 6 }}>
                {t("vorschau.einePipeline")}
              </div>
            )}
          </div>

          {previewLoading && (
            <div className="cs-load">
              <svg viewBox="0 0 24 24"><path d="M21 12a9 9 0 1 1-2.64-6.36"/></svg>
              {t("vorschau.laedt")}
            </div>
          )}

          {previewErr && (
            <div className="cs-msg crit" style={{ marginBottom: "var(--iio-s-4)" }}>
              <svg viewBox="0 0 24 24"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><path d="M12 9v4M12 17h.01"/></svg>
              <div className="tx"><b>{t("vorschau.fehlerTitel")}</b><br/>{previewErr}</div>
            </div>
          )}

          {preview && (
            <>
              {/* Mode selection: extended vs SAP only. Shown before bootstrap while no project
                  exists. Once bound, the mode lives in the monitor. */}
              {(noProject || notBound) && !bootPhase && !bootResult?.ok && (
                <div className="cs-modus">
                  <h3>{t("modus.titel")}</h3>
                  <div className="sub">{t("modus.sub")}</div>
                  <div className="cs-scn-row">
                    <button type="button" className={"cs-scn" + (transportModus === "erweitert" ? " is-active" : "")} onClick={() => setTransportModus("erweitert")} disabled={bootBusy}>
                      <span className="ic"><svg viewBox="0 0 24 24"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg></span>
                      <span className="t">{t("modus.erweiterung")}</span>
                      <span className="d">{t("modus.erweiterungText")}</span>
                      <span className="marke">{t("modus.erweiterungMarke")}</span>
                    </button>
                    <button type="button" className={"cs-scn" + (transportModus === "sap" ? " is-active" : "")} onClick={() => setTransportModus("sap")} disabled={bootBusy}>
                      <span className="ic"><svg viewBox="0 0 24 24"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg></span>
                      <span className="t">{t("modus.sap")}</span>
                      <span className="d">{t("modus.sapText")}</span>
                      <span className="marke">{t("modus.sapMarke")}</span>
                    </button>
                  </div>
                  <div className="cs-vgl">
                    <div className="cs-vgl-head">{t("modus.vergleichTitel")}</div>
                    <div className="cs-vgl-row">
                      <span className="k">{t("modus.transport")}</span>
                      <span className="v">{t("modus.transportErw")}</span>
                      <span className={"v" + (transportModus === "sap" ? "" : " dim")}>{t("modus.transportSap")}</span>
                    </div>
                    <div className="cs-vgl-row">
                      <span className="k">{t("modus.verfuegbarkeit")}</span>
                      <span className="v">{t("modus.verfuegbarkeitErw")}</span>
                      <span className={"v" + (transportModus === "sap" ? "" : " dim")}>{t("modus.verfuegbarkeitSap")}</span>
                    </div>
                    <div className="cs-vgl-row">
                      <span className="k">{t("modus.pd")}</span>
                      <span className="v">{t("modus.pdErw")}</span>
                      <span className={"v" + (transportModus === "sap" ? "" : " dim")}>{t("modus.pdSap")}</span>
                    </div>
                    <div className="cs-vgl-row">
                      <span className="k">{t("modus.werke")}</span>
                      <span className="v">{t("modus.werkeErw")}</span>
                      <span className={"v" + (transportModus === "sap" ? "" : " dim")}>{t("modus.werkeSap")}</span>
                    </div>
                  </div>
                </div>
              )}

              {(noProject || notBound) && !bootPhase && !bootResult?.ok && (
                <div className="cs-boot">
                  <h3>{t("boot.titel")}</h3>
                  <div className="sub">
                    <Trans
                      t={t}
                      i18nKey={tokenMeta?.login ? "boot.subMitKonto" : "boot.subOhneKonto"}
                      values={{ konto: tokenMeta?.login || "" }}
                      components={{ b: <b/>, mono: <span className="mono"/> }}
                    />
                  </div>
                  <div className="cs-row2">
                    <div className="cs-field cs-w-feld">
                      <label>{t("boot.namensLabel")}</label>
                      <input className="cs-input" value={bootName} onChange={e => setBootName(e.target.value)} placeholder={slugify(spaces.find(s => s.id === spaceId)?.name) || t("boot.namensPlatzhalter")} spellCheck={false} autoComplete="off"/>
                    </div>
                    <button type="button" className={"cs-btn prim" + (bootBusy ? " is-loading" : "")} onClick={runBootstrap} disabled={bootBusy || !spaceId}>
                      {bootBusy ? <span className="cs-loading"/> : <svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg>}
                      {t("boot.aufsetzen")}
                    </button>
                  </div>
                  <div className="cs-hint" style={{ marginTop: 8 }}>
                    <Trans
                      t={t}
                      i18nKey="boot.namensHinweis"
                      values={{
                        actions: `${slugify(bootName) || slugify(spaces.find(s => s.id === spaceId)?.name) || "cpi"}-cicd-actions`,
                        content: `${slugify(bootName) || slugify(spaces.find(s => s.id === spaceId)?.name) || "cpi"}-cicd-intsuite`,
                      }}
                      components={{ b: <b/>, mono: <span className="mono"/> }}
                    />
                  </div>
                  {bootResult && bootResult.ok === false && (
                    <div className="cs-msg crit" style={{ marginTop: 10 }}>
                      <svg viewBox="0 0 24 24"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><path d="M12 9v4M12 17h.01"/></svg>
                      <span className="tx">{t("boot.fehlgeschlagen", { grund: bootResult.error })}</span>
                    </div>
                  )}
                </div>
              )}

              {bootPhase && (
                <details className="cs-boot cs-fold" open={bootOffen} onToggle={e => setBootOffen(e.currentTarget.open)}>
                  <summary>
                    <span className="chev"><svg viewBox="0 0 24 24"><polyline points="9 6 15 12 9 18"/></svg></span>
                    {bootPhase === "done" && !bootFehler && <span className="okmark"><svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg></span>}
                    <span>{bootPhase !== "done" ? t("boot.laeuft") : bootFehler ? t("boot.teilweise") : t("boot.fertig")}</span>
                    {bootPhase === "done" && <span className="zsm">{bootFehler ? t("boot.zsmTeilweise") : t("boot.zsmFertig")}</span>}
                  </summary>
                  <div className="cs-boot-run">
                    {[
                      { key: "repos", lbl: t("boot.phaseRepos") },
                      { key: "envs", lbl: t("boot.phaseEnvs") },
                      { key: "check", lbl: t("boot.phaseCheck") },
                    ].map((p, i) => {
                      const ord = { repos: 0, envs: 1, check: 2, done: 3 };
                      const cur = bootPhase ? ord[bootPhase] : 0;
                      // If the run got stuck while filling in the environments,
                      // neither this line nor the convergence check may show a checkmark.
                      const state = bootFehler
                        ? (i === 0 ? "done" : i === 1 ? "bad" : "pend")
                        : i < cur ? "done" : (i === cur && bootPhase !== "done") ? "run" : bootPhase === "done" ? "done" : "pend";
                      return (
                        <div key={p.key} className={"cs-boot-ph " + (state === "done" ? "done" : state === "run" ? "run" : state === "bad" ? "bad" : "")}>
                          <span className="pic">{state === "done" ? <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg> : state === "run" ? <span className="cs-loading"/> : state === "bad" ? <svg viewBox="0 0 24 24"><path d="M18 6 6 18M6 6l12 12"/></svg> : null}</span>
                          <span>{p.lbl}</span>
                        </div>
                      );
                    })}
                    {bootFehler && (
                      <div className="cs-msg crit" style={{ marginTop: "var(--iio-s-3)" }}>
                        <svg viewBox="0 0 24 24"><path d="M12 9v4M12 17h.01"/><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg>
                        <span className="tx">{t("boot.envsFehler", { grund: bootFehler })}</span>
                      </div>
                    )}
                    {bootResult?.ok && (bootResult.steps || []).length > 0 && (
                      <div className="cs-boot-steps">
                        {(bootResult.steps || []).map((st, i) => (
                          <div key={i} className="row" style={{ animationDelay: `${i * 0.12}s` }}>
                            <span className="pic"><svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg></span>
                            <span className="nm">{st.name}</span>
                            <span className="dt">{st.detail}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </details>
              )}
              {bootPhase && convergencePanel}
              {/* Repo privacy shown as either a reassurance or a block warning */}
              {repoPrivate === false && (
                <div className="cs-msg crit" style={{ marginBottom: "var(--iio-s-4)" }}>
                  <svg viewBox="0 0 24 24"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><path d="M12 9v4M12 17h.01"/></svg>
                  <div className="tx">
                    <Trans
                      t={t}
                      i18nKey="repo.nichtPrivat"
                      values={{ repo: `${project?.repoOwner}/${project?.contentRepo}` }}
                      components={{ b: <b/>, mono: <span style={{ fontFamily: "var(--iio-font-mono)" }}/> }}
                    />
                  </div>
                </div>
              )}
              {repoPrivate === true && (
                <div className="cs-msg ok" style={{ marginBottom: "var(--iio-s-4)" }}>
                  <svg viewBox="0 0 24 24"><path d="M20 6 9 17l-5-5"/></svg>
                  <span className="tx">
                    <Trans
                      t={t}
                      i18nKey="repo.privat"
                      values={{ repo: `${project?.repoOwner}/${project?.contentRepo}` }}
                      components={{ mono: <span style={{ fontFamily: "var(--iio-font-mono)" }}/> }}
                    />
                  </span>
                </div>
              )}
              {repoPrivate === null && tokenSet && !noProject && (
                <div className="cs-msg info" style={{ marginBottom: "var(--iio-s-4)" }}>
                  <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
                  <span className="tx">{t("repo.unklar")}</span>
                </div>
              )}
              {repoPrivate === null && !tokenSet && (
                <div className="cs-msg info" style={{ marginBottom: "var(--iio-s-4)" }}>
                  <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
                  <span className="tx">{t("repo.ohneVerbindung")}</span>
                </div>
              )}

              {project && (
                <div className="cs-project">
                  <span className="lbl">{t("vorschau.projekt")}</span>
                  <span className="val">{project.label || project.key}</span>
                  <span style={{ flex: 1 }} />
                  <span className="repo">{project.repoOwner}/{project.contentRepo}</span>
                </div>
              )}

              {!anyStages ? (
                <div className="cs-empty">
                  <b>{t("vorschau.leerTitel")}</b>
                  {/* dev, qas and prod are the raw tag values, not their labels; they stay literal. */}
                  <Trans t={t} i18nKey="vorschau.leerText" components={{ b: <b/> }}/>
                </div>
              ) : (
                <div>
                  {syncSummary && (
                    <div className="cs-syncsummary">
                      <span className="neu">{t("stufe.summeNeu", { n: syncSummary.neu })}</span>
                      <span className="upd">{t("stufe.summeAktualisieren", { n: syncSummary.aktualisieren })}</span>
                      <span className="same">{t("stufe.summeUnveraendert", { n: syncSummary.aktuell })}</span>
                    </div>
                  )}
                  {stages.map(s => {
                    const vars = s.creds?.variables || {};
                    const secs = s.creds?.secrets || {};
                    const warns = s.creds?.warnings || [];
                    const varsList = Object.entries(vars);
                    const secsList = Object.entries(secs);
                    return (
                      <div className="cs-stage" key={s.instanceId}>
                        <div className="cs-stage-head">
                          <span className={"cs-stage-badge " + (s.stage || "dev")}>{stageLabel(s.stage)}</span>
                          <div className="cs-stage-nm">
                            <div className="n">{s.instanceName}</div>
                            <div className="m">{t("stufe.zielEnvironment")} <span style={{ fontFamily: "var(--iio-font-mono)", color: "var(--iio-text-2)" }}>{s.targetEnv}</span></div>
                          </div>
                          {s.creds?.ready
                            ? <span className="cs-stage-status ready"><span className="dot" />{t("stufe.bereit")}</span>
                            : <span className="cs-stage-status miss"><span className="dot" />{t("stufe.unvollstaendig")}</span>}
                          {/* s.sync.status is a raw value from the response and still drives the
                              CSS class; its translated label is shown next to it. */}
                          {s.sync && <span className={"cs-syncbadge " + s.sync.status}>{s.sync.status === "neu" ? t("stufe.wirdAngelegt") : s.sync.status === "aktualisieren" ? t("stufe.wirdAktualisiert") : t("stufe.unveraendert")}</span>}
                        </div>
                        <div className="cs-stage-body">
                          <div className="cs-stage-meta">
                            <span className={"policy" + (s.policyActive ? "" : " off")}><span className="d" />{s.policyActive ? t("stufe.policyAktiv") : t("stufe.nurServiceKey")}</span>
                          </div>

                          {varsList.length > 0 && <>
                            <div className="cs-stage-sub">{t("stufe.variablesTitel")}</div>
                            <div className="cs-kv">
                              {varsList.map(([k, val]) => (
                                <div key={k} style={{ display: "contents" }}>
                                  <div className="k">{k}</div>
                                  <div className={"val" + (val ? " mono" : " miss")}>{val || t("stufe.nichtGepflegt")}</div>
                                </div>
                              ))}
                            </div>
                          </>}

                          {secsList.length > 0 && <>
                            <div className="cs-stage-sub">{t("stufe.secretsTitel")}</div>
                            <div className="cs-kv">
                              {secsList.map(([k, val]) => (
                                <div key={k} style={{ display: "contents" }}>
                                  <div className="k">{k}</div>
                                  <div className={"val " + (val ? "mask" : "miss")}>{val || t("stufe.nichtGepflegt")}</div>
                                </div>
                              ))}
                            </div>
                          </>}

                          {warns.length > 0 && (
                            <div className="cs-msg warn klein">
                              <svg viewBox="0 0 24 24"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><path d="M12 9v4M12 17h.01"/></svg>
                              <div className="tx">
                                <b>{t("stufe.hinweiseTitel")}</b>
                                <ul>{warns.map((w, i) => <li key={i}>{w}</li>)}</ul>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

            </>
          )}
        </section>

        {/* 4 Sync */}
        <section className="cs-sect">
          <div className="cs-sh">
            <div className="cs-no">4</div>
            <div>
              <h2>{t("sync.titel")}</h2>
              <div className="shsub">{t("sync.sub")}</div>
            </div>
          </div>

          {!tokenSet && (
            <div className="cs-hint" style={{ marginBottom: "var(--iio-s-4)" }}>{t("verbindung.zuerstVerbinden")}</div>
          )}
          {tokenSet && !spaceId && (
            <div className="cs-hint" style={{ marginBottom: "var(--iio-s-4)" }}>{t("sync.spaceWaehlen")}</div>
          )}
          {tokenSet && spaceId && !anyStages && !previewLoading && (
            <div className="cs-msg warn" style={{ marginBottom: "var(--iio-s-4)" }}>
              <svg viewBox="0 0 24 24"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><path d="M12 9v4M12 17h.01"/></svg>
              <span className="tx">{t("sync.ohneStages")}</span>
            </div>
          )}

          <div className="cs-actionrow">
            <div className="info">
              {/* notBound is checked first: otherwise this would read "syncing will create N new
                  environments" while the button is locked, an inviting text next to a locked
                  button that never explains why it is locked. */}
              {notBound
                ? t("sync.nichtGebunden")
                : tokenSet && spaceId && anyStages && repoPrivate !== false
                ? (syncSummary
                    ? <Trans t={t} i18nKey="sync.summe" values={{ neu: syncSummary.neu, aktualisieren: syncSummary.aktualisieren }} components={{ b: <b/> }}/>
                    : <Trans t={t} i18nKey="sync.ohneSumme" count={stages.length} values={{ count: stages.length }} components={{ b: <b/> }}/>)
                : repoPrivate === false
                  ? <b>{t("sync.blockiert")}</b>
                  : t("sync.bereitWenn")}
            </div>
            <button type="button" className={"cs-btn prim" + (setupBusy ? " is-loading" : "")} onClick={runSetupClick} disabled={!canSetup}>
              {setupBusy ? <span className="cs-loading" /> : <svg viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m22 4-10 10.01-3-3"/></svg>}
              {setupBusy ? t("sync.laeuft") : t("sync.knopf")}
            </button>
          </div>

          {setupResult && (
            <>
              {setupResult.blocked && (
                <div className="cs-msg crit" style={{ marginTop: "var(--iio-s-4)" }}>
                  <svg viewBox="0 0 24 24"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><path d="M12 9v4M12 17h.01"/></svg>
                  <div className="tx">
                    <b>{t("sync.abbruchTitel")}</b> {setupResult.error || t("sync.abbruchGrund")}
                    {/* visibility is the raw value from GitHub ("public"/"private") and stays literal. */}
                    {setupResult.visibility ? <> {t("sync.sichtbarkeit")} <b>{setupResult.visibility}</b></> : null}
                    <br/><Trans t={t} i18nKey="sync.keineCreds" components={{ b: <b/> }}/>
                  </div>
                </div>
              )}
              {setupResult.steps && setupResult.steps.length > 0 ? (
                <details className="cs-steps">
                  <summary>
                    <span className="chev"><svg viewBox="0 0 24 24"><polyline points="9 6 15 12 9 18"/></svg></span>
                    {setupResult.ok && <span className="okmark"><svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg></span>}
                    <span>
                      {setupResult.ok
                        ? (setupResult.project ? t("sync.fertigMitProjekt", { projekt: setupResult.project }) : t("sync.fertig"))
                        : (setupResult.error ? t("sync.fehlgeschlagenMitGrund", { grund: setupResult.error }) : t("sync.fehlgeschlagen"))}
                    </span>
                    <span className="zsm">
                      {setupResult.steps.every(st => st.ok)
                        ? t("sync.zsmAlleOk", { count: setupResult.steps.length })
                        : t("sync.zsmMitFehlern", { count: setupResult.steps.length, fehler: setupResult.steps.filter(st => !st.ok).length })}
                    </span>
                  </summary>
                  <div className="liste">
                    {setupResult.steps.map((st, i) => (
                      <div key={i} className={"cs-step " + (st.ok ? "ok" : "bad")}>
                        <span className="ic">
                          {st.ok
                            ? <svg viewBox="0 0 24 24"><path d="M20 6 9 17l-5-5"/></svg>
                            : <svg viewBox="0 0 24 24"><path d="M18 6 6 18M6 6l12 12"/></svg>}
                        </span>
                        <span className="nm">{st.step}</span>
                        {typeof st.status !== "undefined" ? <span className="http">HTTP {st.status}</span> : null}
                        {st.error ? <span className="err">{st.error}</span> : null}
                      </div>
                    ))}
                  </div>
                </details>
              ) : (
                setupResult.blocked ? null : <div className="cs-hint" style={{ marginTop: "var(--iio-s-4)" }}>{t("sync.keineSchritte")}</div>
              )}
            </>
          )}
        </section>

        </div>)}

        {tokenSet && scenario === "import" && (<div className="cs-view" key="scn-import" ref={wegViewRef}>

        {/* 3 (scenario 2) import an existing pipeline (GitHub >> webapp) */}
        <section className="cs-sect">
          <div className="cs-sh">
            <div className="cs-no">3</div>
            <div>
              <h2>{t("import.titel")}</h2>
              <div className="shsub">{t("import.sub")}</div>
            </div>
          </div>

          {!tokenSet ? (
            <div className="cs-hint">{t("verbindung.zuerstVerbinden")}</div>
          ) : (
            <>
              <div className="cs-imp-form">
                <div className="cs-field">
                  <label>{t("import.ownerLabel")}</label>
                  <input className="cs-input" value={impOwner} onChange={e => setImpOwner(e.target.value)} placeholder={t("import.ownerPlatzhalter")} spellCheck={false} autoComplete="off"/>
                </div>
                <div className="cs-field">
                  <label>{t("import.repoLabel")}</label>
                  <input className="cs-input" value={impRepo} onChange={e => setImpRepo(e.target.value)} placeholder={t("import.repoPlatzhalter")} spellCheck={false} autoComplete="off" onKeyDown={e => { if (e.key === "Enter") loadImportPreview(); }}/>
                </div>
                <button type="button" className={"cs-btn dark" + (impLoading ? " is-loading" : "")} onClick={loadImportPreview} disabled={impLoading || !impOwner.trim() || !impRepo.trim()}>
                  {impLoading ? <span className="cs-loading" /> : <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>}
                  {t("import.analysieren")}
                </button>
              </div>
              <div className="cs-hint" style={{ marginTop: 8 }}>
                <Trans t={t} i18nKey="import.hinweis" components={{ b: <b/> }}/>
              </div>

              {impPreview && (
                <>
                  {impPreview.repoPrivate === false && (
                    <div className="cs-msg crit" style={{ marginTop: 12 }}>
                      <svg viewBox="0 0 24 24"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><path d="M12 9v4M12 17h.01"/></svg>
                      <div className="tx"><Trans t={t} i18nKey="import.nichtPrivat" components={{ b: <b/> }}/></div>
                    </div>
                  )}

                  {(impPreview.environments || []).length === 0 ? (
                    <div className="cs-empty" style={{ marginTop: "var(--iio-s-4)" }}>{t("import.keineEnvs")}</div>
                  ) : (
                    <div className="cs-imp-tbl">
                      <div className="cs-imp-row head"><span/><span>{t("import.spalteEnvironment")}</span><span>{t("import.spalteStage")}</span><span>{t("import.spalteTenantUrl")}</span><span>{t("import.spalteUser")}</span><span>{t("import.spalteSecrets")}</span></div>
                      {(impPreview.environments || []).map(e => (
                        <div key={e.env} className={"cs-imp-row" + (e.importable ? "" : " dis")}>
                          <span><input type="checkbox" className="cs-check" checked={!!impSel[e.env] && e.importable} disabled={!e.importable} onChange={ev => setImpSel(p => ({ ...p, [e.env]: ev.target.checked }))}/></span>
                          <span style={{ fontWeight: 600 }}>{e.env}</span>
                          {/* stageLabel instead of toUpperCase: otherwise this would read "PREPROD"
                              while the stage is called "PRE-PROD" everywhere else. */}
                          <span className="stage">{e.stage ? stageLabel(e.stage) : "?"}</span>
                          <span className="mono" title={e.tenantUrl}>{e.tenantUrl || t("import.keineApiUrl")}</span>
                          <span className="mono" title={e.apiUser}>{e.apiUser || "-"}{e.policyUser ? t("import.policyUser", { user: e.policyUser }) : ""}</span>
                          <span style={{ color: e.hasApiPassword ? "var(--iio-text-2)" : "color-mix(in srgb, var(--iio-warning) 65%, var(--iio-text-1))" }}>{e.secretNames.length > 0 ? e.secretNames.join(", ") : t("import.keineSecrets")}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="cs-hint" style={{ marginTop: 8 }}>
                    {impPreview.paketConfigs?.length > 0 && (
                      <Trans
                        t={t}
                        i18nKey="import.paketConfigs"
                        count={impPreview.paketConfigs.length}
                        values={{ count: impPreview.paketConfigs.length, pakete: [...new Set(impPreview.paketConfigs.map(p => p.paket))].join(", ") }}
                        components={{ b: <b/> }}
                      />
                    )}
                  </div>

                  {/* Announcement before the write access happens. Import no longer only creates
                      a space and instances, it also commits two workflow files into the customer's
                      repository; without them there is nothing there for the webapp to call. That
                      belongs before the click, not in a message afterwards. */}
                  {(() => {
                    const ext = impPreview.erweiterung;
                    if (!ext) return null;
                    // zustand is a status code from the server response; this only picks
                    // how it is displayed.
                    const zustand = ext.zustand || "unbekannt";
                    const kopf = {
                      fehlt: t("erweiterung.kopfFehlt"),
                      veraltet: t("erweiterung.kopfVeraltet"),
                      aktuell: t("erweiterung.kopfAktuell"),
                      unbekannt: t("erweiterung.kopfUnbekannt"),
                    }[zustand];
                    return (
                      <div className={"cs-ext" + (zustand === "aktuell" ? " is-aktuell" : zustand === "unbekannt" ? " is-unbekannt" : "")}>
                        <div className="h">
                          {zustand === "aktuell" ? (
                            <svg viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m22 4-10 10.01-3-3"/></svg>
                          ) : zustand === "unbekannt" ? (
                            <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01"/></svg>
                          ) : (
                            <svg viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M12 3v12"/><path d="m7 10 5 5 5-5"/></svg>
                          )}
                          {kopf}
                        </div>
                        <div className="lead">
                          {zustand === "unbekannt"
                            ? t("erweiterung.leadUnbekannt", { fehler: ext.fehler || t("erweiterung.keineAntwort") })
                            : <Trans t={t} i18nKey="erweiterung.lead" components={{ b: <b/> }}/>}
                        </div>
                        {(ext.dateien || []).length > 0 && (
                          <ul>
                            {ext.dateien.map(d => (
                              <li key={d.pfad}>
                                {/* d.aktion still drives the CSS class; the raw value stays, its label sits next to it. */}
                                <span className={"akt " + (d.aktion || "")}>{d.aktion === "neu" ? t("erweiterung.aktionNeu") : d.aktion === "aktualisieren" ? t("erweiterung.aktionAktualisieren") : t("erweiterung.aktionUnveraendert")}</span>
                                <span><span className="pfad">{d.pfad}</span> <span className="zw">- {d.zweck}</span></span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {zustand !== "aktuell" && zustand !== "unbekannt" && (
                          <div className="lead" style={{ marginTop: 10 }}>
                            <Trans t={t} i18nKey="erweiterung.branch" values={{ branch: impPreview.erweiterungBranch || impPreview.defaultBranch }} components={{ b: <b/> }}/>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {!impWiz && (
                    <div className="cs-actionrow">
                      <div className="cs-field cs-w-feld">
                        <label>{t("import.spaceNameLabel")}</label>
                        <input className="cs-input" value={impSpaceName} onChange={e => setImpSpaceName(e.target.value)} placeholder={t("import.spaceNamePlatzhalter")}/>
                      </div>
                      {/* The label names the write access as soon as there is one; a message only
                          afterwards would just report a decision the user could no longer make. */}
                      <button type="button" className="cs-btn prim" onClick={startGuided} disabled={impBusy || impPreview.repoPrivate === false || (impPreview.environments || []).every(e => !impSel[e.env] || !e.importable)}>
                        <svg viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/></svg>
                        {impPreview.erweiterung && impPreview.erweiterung.zustand !== "aktuell"
                          ? t("import.knopfMitErweiterung")
                          : t("import.knopf")}
                      </button>
                    </div>
                  )}

                  {impWiz && (() => {
                    const env = impWiz.envs[impWiz.idx];
                    const f = impWiz.forms[env.env];
                    const setField = (k, v) => setImpWiz(w => w ? { ...w, forms: { ...w.forms, [env.env]: { ...w.forms[env.env], [k]: v } } } : w);
                    return (
                      <div className="cs-wiz">
                        <div className="cs-wiz-head">
                          <h3>{t("assistent.kopf", { nr: impWiz.idx + 1, gesamt: impWiz.envs.length, env: env.env })}</h3>
                          <div className="sub">{t("assistent.sub", { space: impSpaceName.trim(), stufe: stageLabel(env.stage) })}</div>
                        </div>
                        <div className="cs-wiz-rail">
                          {impWiz.envs.map((e, i) => (
                            <Fragment key={e.env}>
                              {i > 0 && <span className={"cs-wiz-conn" + (i <= impWiz.idx ? " fill" : "")}><i/></span>}
                              <span className={"cs-wiz-node" + (i === impWiz.idx ? " act" : i < impWiz.idx ? " done" : "")}>
                                <span className="cs-wiz-dot">{i < impWiz.idx
                                  ? <svg viewBox="0 0 24 24" style={{ width: 12, height: 12, stroke: "currentColor", fill: "none", strokeWidth: 2.6, strokeLinecap: "round", strokeLinejoin: "round" }}><polyline points="20 6 9 17 4 12"/></svg>
                                  : i + 1}</span>
                                <span className="lbl">{stageLabel(e.stage)}</span>
                              </span>
                            </Fragment>
                          ))}
                        </div>
                        <div className="cs-wiz-step" key={env.env}>
                          <CicdImportInstanceForm form={f} setField={setField} env={env} addToast={addToast}/>
                        </div>
                        <div className="cs-wiz-foot">
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <button type="button" className="cs-btn ghost" onClick={() => impWiz.idx === 0 ? setImpWiz(null) : setImpWiz(w => ({ ...w, idx: w.idx - 1 }))} disabled={impBusy}>
                              {impWiz.idx === 0 ? t("gemeinsam:aktion.abbrechen") : t("gemeinsam:aktion.zurueck")}
                            </button>
                            <button type="button" className="cs-linkbtn" onClick={() => wizNext(true)} disabled={impBusy}>{t("assistent.spaeterNachtragen")}</button>
                          </div>
                          <button type="button" className={"cs-btn prim" + (impBusy ? " is-loading" : "")} onClick={() => wizNext(false)} disabled={impBusy}>
                            {impBusy ? <span className="cs-loading"/> : null}
                            {impWiz.idx === impWiz.envs.length - 1 ? t("assistent.abschluss") : t("assistent.uebernehmen")}
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}

              {impResult?.ok && (
                <>
                  <div className="cs-msg ok" style={{ marginTop: "var(--iio-s-4)" }}>
                    <svg viewBox="0 0 24 24"><path d="M20 6 9 17l-5-5"/></svg>
                    <span className="tx"><b>{t("import.fertigTitel")}</b> <Trans t={t} i18nKey="import.fertigText" values={{ space: impResult.spaceName, instanzen: (impResult.created || []).map(c => c.name).join(", "), projekt: impResult.project }} components={{ b: <b/> }}/></span>
                  </div>
                  {(impResult.created || []).some(c => c.credsComplete === false) && (
                    <div className="cs-msg warn" style={{ marginTop: 10 }}>
                      <svg viewBox="0 0 24 24"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><path d="M12 9v4M12 17h.01"/></svg>
                      <span className="tx"><b>{t("import.offenTitel")}</b> {t("import.offenText", { namen: (impResult.created || []).filter(c => c.credsComplete === false).map(c => c.name).join(", ") })}</span>
                    </div>
                  )}
                </>
              )}
              {impResult && impResult.ok === false && (
                <div className="cs-msg crit" style={{ marginTop: 10 }}>
                  <svg viewBox="0 0 24 24"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><path d="M12 9v4M12 17h.01"/></svg>
                  <span className="tx">{t("import.fehlgeschlagen", { grund: impResult.error })}</span>
                </div>
              )}

              {convergencePanel}
            </>
          )}
        </section>

        </div>)}

      </div>

      <ToastContainer toasts={toasts} />
    </div>
  );
}
